// File: src/pages/organizations/OrganizationSubscription.js
// Route: /organizations/:orgId/subscription

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  deleteField,
  runTransaction,
  query,
  where,
  orderBy,
  limit,
  arrayUnion,
} from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/authContext';
import { convertProvisionalLicenses } from '../../utils/provisionalConversion';
// AddMemberModal is used on the org page; invite flow now has its own page at /organization/:orgId/invite

const OrganizationSubscription = ({ embedded = false, onBack }) => {
  const { orgId: routeOrgId } = useParams();
  const orgId = routeOrgId; // when embedded, parent could pass orgId prop; for now always use route
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showTestSubscriptionModal, setShowTestSubscriptionModal] = useState(false);
  const [stripeProducts, setStripeProducts] = useState([]);
  const [testStripeProducts, setTestStripeProducts] = useState([]);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [testStripeLoading, setTestStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');
  // Product categories from Firestore (set by admin in Products Management)
  const [productCategories, setProductCategories] = useState(null);
  const [testStripeError, setTestStripeError] = useState('');
  const [currentStep, setCurrentStep] = useState('platform'); // 'platform' or 'disciplemakers'
  const [testCurrentStep, setTestCurrentStep] = useState('platform'); // For test modal
  const [billingPeriod, setBillingPeriod] = useState('annual'); // 'annual' or 'monthly' - default to annual
  const [testBillingPeriod, setTestBillingPeriod] = useState('annual'); // For test modal
  const [selectedPlatformTier, setSelectedPlatformTier] = useState(null); // 'small-pilot', 'standard', 'growing', 'large-multisite'
  const [testSelectedPlatformTier, setTestSelectedPlatformTier] = useState(null); // For test modal
  const [selectedDisciplemakerPack, setSelectedDisciplemakerPack] = useState('first-five'); // 'first-five', 'starter', 'growth', 'active', 'city' - default to first-five
  const [testSelectedDisciplemakerPack, setTestSelectedDisciplemakerPack] = useState('first-five'); // For test modal
  const [couponCode, setCouponCode] = useState('');
  const [testCouponCode, setTestCouponCode] = useState('');
  const [platformProductName, setPlatformProductName] = useState('');
  const [disciplemakerPackProductName, setDisciplemakerPackProductName] = useState('');
  const [loadingProductNames, setLoadingProductNames] = useState(false);
  const [allocationHistory, setAllocationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return ['subscription', 'licenses', 'billing'].includes(tab) ? tab : 'subscription';
  });
  const [billingHistory, setBillingHistory] = useState([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [pendingInvitations, setPendingInvitations] = useState([]);

  // Load product categories from Firestore (admin-configured)
  const loadProductCategories = async () => {
    try {
      const catDoc = await getDoc(doc(db, 'appConfig', 'subscriptionProducts'));
      if (catDoc.exists()) {
        setProductCategories(catDoc.data());
      }
    } catch (err) {
      console.error('Error loading product categories:', err);
    }
  };

  useEffect(() => {
    loadOrganization();
    loadMembers();
    loadPendingInvitations();
    loadStripeProducts();
    loadProductCategories();
  }, [orgId]);

  // Refresh data when returning from checkout (check for session_id in URL)
  useEffect(() => {
    const checkForReturnFromCheckout = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const success = urlParams.get('success');
      
      if (sessionId || success) {
        // User just returned from checkout - refresh organization data
        console.log('Returned from checkout, refreshing organization data...');
        loadOrganization();
        // Clean up URL params
        window.history.replaceState({}, '', window.location.pathname);
      }
    };

    checkForReturnFromCheckout();
  }, [orgId]);

  useEffect(() => {
    // Load product names when organization license is available
    if (organization?.license) {
      loadProductNames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.license]);

  // Auto-convert provisional licenses when seats become available
  useEffect(() => {
    if (!organization?.license || !orgId) return;
    const totalSeats = organization.license.disciplemakers ?? organization.license.seats ?? 0;
    const usedSeats = organization.license.disciplemakersUsed ?? organization.license.seatsUsed ?? 0;
    const available = totalSeats - usedSeats;
    if (available > 0) {
      convertProvisionalLicenses(orgId).then((converted) => {
        if (converted > 0) {
          console.log(`Converted ${converted} provisional license(s) to full`);
          loadOrganization();
          loadMembers();
        }
      });
    }
  }, [organization?.license, orgId]);

  // Auto-correct billing period when products for the CURRENT STEP don't have pricing
  // for the selected period. E.g., seat packs only have monthly → switch from annual to monthly.
  useEffect(() => {
    if (stripeProducts.length === 0) return;

    // Filter products relevant to the current step using categories (or fallback heuristics)
    const catReady = productCategories && (
      (productCategories.platformProductIds?.length > 0) ||
      (productCategories.seatPackProductIds?.length > 0) ||
      !!productCategories.firstFiveProductId
    );

    let relevant;
    if (currentStep === 'disciplemakers') {
      relevant = stripeProducts.filter(p => {
        if (catReady) {
          if (productCategories.archivedProductIds?.includes(p.id)) return false;
          if (productCategories.platformProductIds?.includes(p.id)) return false;
          if (productCategories.firstFiveProductId === p.id) return false;
          return productCategories.seatPackProductIds?.includes(p.id);
        }
        // Fallback: name heuristics for seat packs (excluding platform & first-five)
        const name = (p.name || '').toLowerCase();
        if (name.includes('platform') || name.includes('pilot') || name.includes('standard') || name.includes('growing') || name.includes('large') || name.includes('multisite')) return false;
        if (name.includes('first five') || name.includes('first 5') || name.includes('5 free')) return false;
        return name.includes('seat') || name.includes('pack') || name.includes('disciplemaker') || name.includes('mini');
      });
    } else {
      relevant = stripeProducts.filter(p => {
        if (catReady) {
          if (productCategories.archivedProductIds?.includes(p.id)) return false;
          return productCategories.platformProductIds?.includes(p.id);
        }
        const name = (p.name || '').toLowerCase();
        return name.includes('platform') || name.includes('pilot') || name.includes('standard') || name.includes('growing') || name.includes('large') || name.includes('multisite');
      });
    }

    if (relevant.length === 0) return;
    const hasMonthly = relevant.some(p => (p.prices || []).some(pr => pr.recurring?.interval === 'month'));
    const hasAnnual = relevant.some(p => (p.prices || []).some(pr => pr.recurring?.interval === 'year'));

    if (billingPeriod === 'annual' && !hasAnnual && hasMonthly) {
      setBillingPeriod('monthly');
    } else if (billingPeriod === 'monthly' && !hasMonthly && hasAnnual) {
      setBillingPeriod('annual');
    }
  }, [stripeProducts, billingPeriod, currentStep, productCategories]);

  // Same auto-correction for test modal
  useEffect(() => {
    if (testStripeProducts.length === 0) return;

    const catReady = productCategories && (
      (productCategories.platformProductIds?.length > 0) ||
      (productCategories.seatPackProductIds?.length > 0) ||
      !!productCategories.firstFiveProductId
    );

    let relevant;
    if (testCurrentStep === 'disciplemakers') {
      relevant = testStripeProducts.filter(p => {
        if (catReady) {
          if (productCategories.archivedProductIds?.includes(p.id)) return false;
          if (productCategories.platformProductIds?.includes(p.id)) return false;
          if (productCategories.firstFiveProductId === p.id) return false;
          return productCategories.seatPackProductIds?.includes(p.id);
        }
        const name = (p.name || '').toLowerCase();
        if (name.includes('platform') || name.includes('pilot') || name.includes('standard') || name.includes('growing') || name.includes('large') || name.includes('multisite')) return false;
        if (name.includes('first five') || name.includes('first 5') || name.includes('5 free')) return false;
        return name.includes('seat') || name.includes('pack') || name.includes('disciplemaker') || name.includes('mini');
      });
    } else {
      relevant = testStripeProducts.filter(p => {
        if (catReady) {
          if (productCategories.archivedProductIds?.includes(p.id)) return false;
          return productCategories.platformProductIds?.includes(p.id);
        }
        const name = (p.name || '').toLowerCase();
        return name.includes('platform') || name.includes('pilot') || name.includes('standard') || name.includes('growing') || name.includes('large') || name.includes('multisite');
      });
    }

    if (relevant.length === 0) return;
    const hasMonthly = relevant.some(p => (p.prices || []).some(pr => pr.recurring?.interval === 'month'));
    const hasAnnual = relevant.some(p => (p.prices || []).some(pr => pr.recurring?.interval === 'year'));

    if (testBillingPeriod === 'annual' && !hasAnnual && hasMonthly) {
      setTestBillingPeriod('monthly');
    } else if (testBillingPeriod === 'monthly' && !hasMonthly && hasAnnual) {
      setTestBillingPeriod('annual');
    }
  }, [testStripeProducts, testBillingPeriod, testCurrentStep, productCategories]);

  const loadStripeProducts = async () => {
    setStripeLoading(true);
    setStripeError('');
    try {
      const listProducts = httpsCallable(functions, 'listStripeProducts');
      const result = await listProducts({ limit: 100 });
      const products = result.data?.products || [];
      setStripeProducts(products);

      // Products are filtered and sorted later in the component
    } catch (error) {
      console.error('Error loading Stripe products:', error);
      setStripeError('Failed to load Stripe products.');
    } finally {
      setStripeLoading(false);
    }
  };

  const loadProductNames = async () => {
    const license = organization?.license;
    if (!license) return;

    setLoadingProductNames(true);
    try {
      const listProducts = httpsCallable(functions, 'listStripeProducts');
      const result = await listProducts({ limit: 100 });
      const products = result.data?.products || [];

      // Find platform product by price ID
      if (license.platformPriceId) {
        const platformProduct = products.find((p) =>
          p.prices?.some((price) => price.id === license.platformPriceId)
        );
        if (platformProduct) {
          setPlatformProductName(platformProduct.name || 'Platform');
        }
      }

      // Find disciplemaker pack product by price ID
      if (license.disciplemakerPackPriceId) {
        const packProduct = products.find((p) =>
          p.prices?.some((price) => price.id === license.disciplemakerPackPriceId)
        );
        if (packProduct) {
          setDisciplemakerPackProductName(packProduct.name || 'Disciplemaker Pack');
        }
      }
    } catch (error) {
      console.error('Error loading product names:', error);
    } finally {
      setLoadingProductNames(false);
    }
  };

  const loadBillingHistory = async () => {
    setLoadingBilling(true);
    setBillingError('');
    try {
      const getBillingHistory = httpsCallable(functions, 'getOrganizationBillingHistory');
      const result = await getBillingHistory({ orgId });
      setBillingHistory(result.data?.invoices || []);
    } catch (error) {
      console.error('Error loading billing history:', error);
      const msg = error?.message || error?.code || 'Unknown error';
      setBillingError(`Failed to load billing history: ${msg}`);
    } finally {
      setLoadingBilling(false);
    }
  };

  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatInvoiceDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-800',
      open: 'bg-blue-100 text-blue-800',
      draft: 'bg-gray-100 text-gray-800',
      uncollectible: 'bg-red-100 text-red-800',
      void: 'bg-gray-100 text-gray-500',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const loadOrganization = async () => {
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (orgDoc.exists()) {
        const orgData = { id: orgDoc.id, ...orgDoc.data() };
        setOrganization(orgData);
        // Debug: Log license data with full details
        const license = orgData.license || {};
        console.log('Organization loaded:', {
          orgId: orgDoc.id,
          hasLicense: !!orgData.license,
          licenseStatus: license.status,
          licenseKeys: Object.keys(license),
          fullLicense: license,
          stripeSubscriptionId: license.stripeSubscriptionId,
          stripeCustomerId: license.stripeCustomerId,
        });
      } else {
        console.warn('Organization document does not exist:', orgId);
      }
    } catch (error) {
      console.error('Error loading organization:', error);
      alert('Error loading organization');
    }
  };

  const loadMembers = async () => {
    try {
      const membersRef = collection(db, 'organizations', orgId, 'members');
      const snapshot = await getDocs(membersRef);

      // Load all license allocations to cross-reference
      const allocationsRef = collection(db, 'organizations', orgId, 'licenseAllocations');
      const allocationsSnap = await getDocs(allocationsRef);
      const allocatedUserIds = new Set(allocationsSnap.docs.map(d => d.id));

      const membersList = [];

      for (const memberDoc of snapshot.docs) {
        const memberData = memberDoc.data();
        if (memberData.status === 'inactive') continue;

        const userId = memberDoc.id;
        const userDoc = await getDoc(doc(db, 'users', userId));

        // Auto-heal: if licenseAllocations says allocated but member doc disagrees
        const hasAllocation = allocatedUserIds.has(userId);
        let licenseAllocated = memberData.licenseAllocated || false;
        let licenseStatus = memberData.licenseStatus || '';

        if (hasAllocation && !licenseAllocated && licenseStatus !== 'revoked') {
          licenseAllocated = true;
          licenseStatus = 'allocated';
          // Fix the member document in the background
          try {
            await updateDoc(doc(db, 'organizations', orgId, 'members', userId), {
              licenseAllocated: true,
              licenseStatus: 'allocated',
            });
          } catch (healErr) {
            console.warn('Auto-heal member doc failed for', userId, healErr);
          }
        }

        membersList.push({
          userId,
          ...memberData,
          licenseAllocated,
          licenseStatus,
          userName: userDoc.exists() ? userDoc.data().name : 'Unknown',
          userEmail: userDoc.exists() ? userDoc.data().email : '',
        });
      }

      membersList.sort((a, b) => a.userName.localeCompare(b.userName));
      setMembers(membersList);
    } catch (error) {
      console.error('Error loading members:', error);
      alert('Error loading members');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingInvitations = async () => {
    try {
      const invitesRef = collection(db, 'organizations', orgId, 'invitations');
      const q = query(invitesRef, where('status', '==', 'pending'));
      const snapshot = await getDocs(q);
      const invites = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      }));
      // Sort by createdAt descending
      invites.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bTime - aTime;
      });
      setPendingInvitations(invites);
    } catch (err) {
      console.error('Error loading pending invitations:', err);
    }
  };

  // Revoke an invitation and release the held license seat
  const revokeInvitation = async (inviteId) => {
    try {
      setProcessing(`revoke-${inviteId}`);

      let inviteEmail = null;
      await runTransaction(db, async (transaction) => {
        const inviteRef = doc(db, 'organizations', orgId, 'invitations', inviteId);
        const inviteSnap = await transaction.get(inviteRef);
        if (!inviteSnap.exists() || inviteSnap.data().status !== 'pending') {
          throw new Error('Invitation is no longer pending.');
        }
        inviteEmail = inviteSnap.data().email;

        // Update invitation status
        transaction.update(inviteRef, {
          status: 'canceled',
          canceledAt: serverTimestamp(),
          canceledBy: currentUser.uid,
        });

        // Delete license reservation if one exists
        const reservationRef = doc(db, 'organizations', orgId, 'licenseReservations', inviteId);
        transaction.delete(reservationRef);
      });

      // Also update the user-side mirror so the invite disappears from their app
      if (inviteEmail) {
        try {
          const userQ = query(collection(db, 'users'), where('email', '==', inviteEmail.toLowerCase()));
          const userSnap = await getDocs(userQ);
          if (!userSnap.empty) {
            const targetUserId = userSnap.docs[0].id;
            await updateDoc(doc(db, 'users', targetUserId, 'invitations', inviteId), {
              status: 'canceled',
              canceledAt: serverTimestamp(),
              canceledBy: currentUser.uid,
            });
          }
        } catch (mirrorErr) {
          console.warn('Could not update user-side invite mirror:', mirrorErr);
        }
      }

      // Refresh data
      await loadPendingInvitations();
    } catch (err) {
      console.error('Error revoking invitation:', err);
      alert(`Error revoking invitation: ${err.message}`);
    } finally {
      setProcessing(null);
    }
  };

  const loadAllocationHistory = async () => {
    try {
      setLoadingHistory(true);
      const historyRef = collection(db, 'organizations', orgId, 'licenseAllocationHistory');
      const historyQuery = query(historyRef, orderBy('timestamp', 'desc'), limit(50));
      const snapshot = await getDocs(historyQuery);
      
      const historyList = [];
      for (const historyDoc of snapshot.docs) {
        const historyData = historyDoc.data();
        const userDoc = await getDoc(doc(db, 'users', historyData.userId));
        const userName = userDoc.exists() ? userDoc.data().name : 'Unknown User';
        
        historyList.push({
          id: historyDoc.id,
          ...historyData,
          userName,
        });
      }
      
      setAllocationHistory(historyList);
    } catch (error) {
      console.error('Error loading allocation history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const allocateLicense = async (userId) => {
    const license = organization?.license;
    if (!license) {
      alert('No subscription found for this organization');
      return;
    }

    // Use disciplemakers terminology (preferred) with fallback to seats (legacy)
    const totalLicenses = license.disciplemakers ?? license.seats ?? 0;
    const usedLicenses = license.disciplemakersUsed ?? license.seatsUsed ?? 0;

    if (usedLicenses >= totalLicenses) {
      alert('All disciplemaker licenses are allocated. Upgrade to add more licenses.');
      return;
    }

    try {
      setProcessing(userId);

      // Use Firestore transaction for atomic operations
      const orgRef = doc(db, 'organizations', orgId);
      const memberRef = doc(db, 'organizations', orgId, 'members', userId);
      const allocationRef = doc(db, 'organizations', orgId, 'licenseAllocations', userId);
      const allocationHistoryRef = collection(db, 'organizations', orgId, 'licenseAllocationHistory');

      await runTransaction(db, async (transaction) => {
        // Read current state
        const orgSnap = await transaction.get(orgRef);
        const memberSnap = await transaction.get(memberRef);
        const allocationSnap = await transaction.get(allocationRef);

        if (!orgSnap.exists()) {
          throw new Error('Organization not found');
        }

        const orgData = orgSnap.data();
        const currentLicense = orgData?.license || {};
        const currentTotal = currentLicense.disciplemakers ?? currentLicense.seats ?? 0;
        const currentUsed = currentLicense.disciplemakersUsed ?? currentLicense.seatsUsed ?? 0;

        // Double-check availability (prevent race conditions)
        if (currentUsed >= currentTotal) {
          throw new Error('All licenses are allocated');
        }

        // Check if already allocated
        if (allocationSnap.exists() || memberSnap.data()?.licenseAllocated) {
          throw new Error('License already allocated to this user');
        }

        // Get user info for audit trail (use transaction.get for consistency)
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await transaction.get(userRef);
        const allocatedByName = userDoc.exists() ? userDoc.data().name : currentUser.email || 'Unknown';

        // Create allocation record with full audit trail
        transaction.set(allocationRef, {
          userId,
          status: 'allocated',
          allocatedAt: serverTimestamp(),
          allocatedBy: currentUser.uid,
          allocatedByName: allocatedByName,
          reason: 'manual_allocation',
          organizationId: orgId,
          expiresAt: null,
          revokedAt: null,
          revokedBy: null,
        });

        // Create history record for tracking
        const historyDocRef = doc(allocationHistoryRef);
        transaction.set(historyDocRef, {
          userId,
          action: 'allocated',
          status: 'allocated',
          allocatedBy: currentUser.uid,
          allocatedByName: allocatedByName,
          reason: 'manual_allocation',
          organizationId: orgId,
          timestamp: serverTimestamp(),
        });

        // Update member document
        transaction.update(memberRef, {
          licenseAllocated: true,
          licenseStatus: 'allocated',
          licenseAllocatedAt: serverTimestamp(),
          licenseExpiresAt: null,
        });

        // Update license counts atomically
        const newUsed = currentUsed + 1;
        const newAvailable = currentTotal - newUsed;
        transaction.update(orgRef, {
          'license.disciplemakersUsed': newUsed,
          'license.disciplemakersAvailable': newAvailable,
          'license.seatsUsed': newUsed, // Legacy field
          'license.seatsAvailable': newAvailable, // Legacy field
          'license.updatedAt': serverTimestamp(),
        });
      });

      await syncUserSideData(userId);
      await loadOrganization();
      await loadMembers();
    } catch (error) {
      const isAlreadyAllocated = error.message?.includes('already allocated');
      if (isAlreadyAllocated) {
        // License exists but user-side data may be out of sync -- fix it
        await syncUserSideData(userId);
        await loadOrganization();
        await loadMembers();
      }
      console.error('Error allocating license:', error);
      alert(error.message || 'Error allocating license');
    } finally {
      setProcessing(null);
    }
  };

  const syncUserSideData = async (userId) => {
    try {
      const userOrgRef = doc(db, 'users', userId, 'organizations', orgId);
      await setDoc(userOrgRef, {
        status: 'active',
        licenseAllocated: true,
        name: organization?.name || organization?.displayName || '',
      }, { merge: true });
    } catch (userSubError) {
      console.warn('Could not update user org subcollection (will auto-heal on next login):', userSubError);
    }

    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, { activeOrgs: arrayUnion(orgId) });
    } catch (activeOrgsError) {
      console.warn('Could not update user activeOrgs (will auto-heal on next login):', activeOrgsError);
    }

    try {
      const memberRef = doc(db, 'organizations', orgId, 'members', userId);
      await updateDoc(memberRef, { status: 'active' });
    } catch (statusError) {
      console.warn('Could not update member status:', statusError);
    }
  };

  const releaseLicense = async (userId) => {
    const license = organization?.license;
    if (!license) return;

    try {
      setProcessing(userId);

      // Use Firestore transaction for atomic operations
      const orgRef = doc(db, 'organizations', orgId);
      const memberRef = doc(db, 'organizations', orgId, 'members', userId);
      const allocationRef = doc(db, 'organizations', orgId, 'licenseAllocations', userId);
      const allocationHistoryRef = collection(db, 'organizations', orgId, 'licenseAllocationHistory');

      await runTransaction(db, async (transaction) => {
        // Read current state
        const orgSnap = await transaction.get(orgRef);
        const memberSnap = await transaction.get(memberRef);
        const allocationSnap = await transaction.get(allocationRef);

        if (!orgSnap.exists()) {
          throw new Error('Organization not found');
        }

        const orgData = orgSnap.data();
        const currentLicense = orgData?.license || {};
        const currentTotal = currentLicense.disciplemakers ?? currentLicense.seats ?? 0;
        const currentUsed = currentLicense.disciplemakersUsed ?? currentLicense.seatsUsed ?? 0;

        // Check if license is actually allocated
        if (!allocationSnap.exists() && !memberSnap.data()?.licenseAllocated) {
          throw new Error('No license allocated to this user');
        }

        // Get user info for audit trail (use transaction.get for consistency)
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await transaction.get(userRef);
        const releasedByName = userDoc.exists() ? userDoc.data().name : currentUser.email || 'Unknown';

        // Create history record for tracking
        const historyDocRef = doc(allocationHistoryRef);
        transaction.set(historyDocRef, {
          userId,
          action: 'revoked',
          revokedBy: currentUser.uid,
          revokedByName: releasedByName,
          reason: 'manual_revoke',
          organizationId: orgId,
          timestamp: serverTimestamp(),
        });

        // Update allocation record to revoked (instead of deleting)
        transaction.update(allocationRef, {
          status: 'revoked',
          revokedAt: serverTimestamp(),
          revokedBy: currentUser.uid,
        });

        // Update member document
        transaction.update(memberRef, {
          licenseAllocated: false,
          licenseStatus: 'revoked',
          licenseAllocatedAt: deleteField(),
          licenseExpiresAt: null,
        });

        // Update license counts atomically
        const newUsed = Math.max(0, currentUsed - 1);
        const newAvailable = currentTotal - newUsed;
        transaction.update(orgRef, {
          'license.disciplemakersUsed': newUsed,
          'license.disciplemakersAvailable': newAvailable,
          'license.seatsUsed': newUsed, // Legacy field
          'license.seatsAvailable': newAvailable, // Legacy field
          'license.updatedAt': serverTimestamp(),
        });
      });

      await loadOrganization();
      await loadMembers();
    } catch (error) {
      console.error('Error releasing license:', error);
      alert(error.message || 'Error releasing license');
    } finally {
      setProcessing(null);
    }
  };

  const handleUpgrade = (startStep = 'platform') => {
    // Pre-fill current selections if subscription exists
    if (hasSubscription && license) {
      if (license.platformTier) {
        setSelectedPlatformTier(license.platformTier);
      }

      const currentPack = license.disciplemakerPackTier || license.seatPackTier;
      if (currentPack && currentPack !== 'first-five') {
        setSelectedDisciplemakerPack(currentPack);
      } else {
        setSelectedDisciplemakerPack('first-five');
      }

      setBillingPeriod(license.billingCycle || 'annual');
    }

    setCurrentStep(startStep);
    setShowSubscriptionModal(true);
  };

  const handleTestUpgrade = async () => {
    setShowTestSubscriptionModal(true);
    // Load test products when opening test modal
    await loadTestStripeProducts();
  };

  const loadTestStripeProducts = async () => {
    setTestStripeLoading(true);
    setTestStripeError('');
    try {
      const listProducts = httpsCallable(functions, 'listStripeProducts');
      const result = await listProducts({ limit: 100 });
      const products = result.data?.products || [];
      setTestStripeProducts(products);
    } catch (error) {
      console.error('Error loading test Stripe products:', error);
      setTestStripeError('Failed to load Stripe products. Make sure you are logged in as a Super Admin.');
    } finally {
      setTestStripeLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowSubscriptionModal(false);
    setCouponCode('');
    setCurrentStep('platform');
    setSelectedPlatformTier(null);
    setSelectedDisciplemakerPack('first-five'); // Reset to first-five (default)
  };

  const handleCloseTestModal = () => {
    setShowTestSubscriptionModal(false);
    setTestCouponCode('');
    setTestCurrentStep('platform');
    setTestSelectedPlatformTier(null);
    setTestSelectedDisciplemakerPack('first-five'); // Reset to first-five (default)
  };

  // Phase 1: Platform-only checkout (card entry via Stripe Checkout)
  const handleSubscribe = async () => {
    try {
      setProcessing('subscribe');
      if (!selectedPlatformTier) {
        alert('Please select a platform tier.');
        setProcessing(null);
        return;
      }

      const platformPriceId = selectedPlatformPrice?.id;
      if (!platformPriceId) {
        alert('Error: Could not find price for selected platform tier and billing period.');
        setProcessing(null);
        return;
      }

      // Platform-only checkout — no seat pack in this subscription
      const createCheckoutSession = httpsCallable(functions, 'createOrganizationCheckoutSession');
      const result = await createCheckoutSession({
        orgId,
        platformTier: selectedPlatformTier,
        platformPriceId: platformPriceId,
        billingPeriod: billingPeriod,
        couponCode: couponCode.trim() || undefined,
      });

      if (result.data?.url) {
        window.location.href = result.data.url;
      } else {
        alert('Error creating checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert(`Error: ${error.message || 'Failed to create checkout session'}`);
    } finally {
      setProcessing(null);
    }
  };

  // Phase 2: Add/Upgrade seat pack — uses card on file, no Stripe redirect
  const handleAddSeatPack = async () => {
    try {
      setProcessing('add-seatpack');
      if (!selectedDisciplemakerPack || selectedDisciplemakerPack === 'first-five') {
        alert('Please select a disciplemaker pack.');
        setProcessing(null);
        return;
      }

      const seatPackPriceId = selectedDisciplemakerPrice?.id;
      if (!seatPackPriceId) {
        alert('Error: Could not find price for selected disciplemaker pack.');
        setProcessing(null);
        return;
      }

      // Confirm with the user — no card entry needed
      const packProduct = seatPackProducts.find(p => getSeatPackTier(p) === selectedDisciplemakerPack);
      const packName = packProduct?.name || selectedDisciplemakerPack;
      const priceLabel = selectedDisciplemakerPrice
        ? `${formatCurrency(selectedDisciplemakerPrice.unitAmount, selectedDisciplemakerPrice.currency)}${formatIntervalLabel(selectedDisciplemakerPrice)}`
        : '';
      const confirmMsg = `Add "${packName}" for ${priceLabel}?\n\nYour card on file will be charged. This creates a separate subscription that can be managed independently from your platform subscription.`;

      if (!window.confirm(confirmMsg)) {
        setProcessing(null);
        return;
      }

      const addSeatPack = httpsCallable(functions, 'addSeatPackSubscription');
      const result = await addSeatPack({
        orgId,
        seatPackTier: selectedDisciplemakerPack,
        seatPackPriceId: seatPackPriceId,
        billingPeriod: billingPeriod,
      });

      if (result.data?.subscriptionId) {
        alert(`✅ Seat pack added! ${result.data.disciplemakers} disciplemaker licenses now available.`);
        handleCloseModal();
        await loadOrganization();
        await loadMembers();
      } else {
        alert('Error adding seat pack');
      }
    } catch (error) {
      console.error('Error adding seat pack:', error);
      alert(`Error: ${error.message || 'Failed to add seat pack'}`);
    } finally {
      setProcessing(null);
    }
  };

  // Cancel seat pack subscription
  const handleCancelSeatPack = async () => {
    const confirmMsg = 'Cancel your disciplemaker pack subscription?\n\nYou will retain access until the end of the current billing period, then your licenses will drop to the free 5.';
    if (!window.confirm(confirmMsg)) return;

    try {
      setProcessing('cancel-seatpack');
      const cancelSeatPack = httpsCallable(functions, 'cancelSeatPackSubscription');
      await cancelSeatPack({ orgId, immediate: false });
      alert('Seat pack will be cancelled at the end of the current billing period.');
      await loadOrganization();
    } catch (error) {
      console.error('Error cancelling seat pack:', error);
      alert(`Error: ${error.message || 'Failed to cancel seat pack'}`);
    } finally {
      setProcessing(null);
    }
  };

  // Test subscription handler — Phase 1: Platform-only checkout (matches production dual-sub flow)
  const handleTestSubscribe = async () => {
    try {
      setProcessing('test-subscribe');
      // Platform is required
      if (!testSelectedPlatformTier) {
        alert('Please select a platform tier.');
        setProcessing(null);
        return;
      }
      
      // Get products from dynamically loaded test products
      const testPlatformProducts = testStripeProducts
        .filter((product) => isPlatformProduct(product) && !isArchivedProduct(product))
        .sort(sortByPreferredOrder(platformOrder));
      
      // Get selected platform product with price for current billing period
      const selectedTestPlatformProduct = testPlatformProducts.find(p => getPlatformTier(p) === testSelectedPlatformTier);
      const selectedTestPlatformPrice = selectedTestPlatformProduct 
        ? getPriceForBillingPeriod(selectedTestPlatformProduct, testBillingPeriod)
        : null;
      
      console.log('Test Modal - Platform Product:', selectedTestPlatformProduct?.name);
      console.log('Test Modal - Platform Price Object:', selectedTestPlatformPrice);
      console.log('Test Modal - Platform Price ID:', selectedTestPlatformPrice?.id);
      
      if (!selectedTestPlatformPrice) {
        alert('Error: Could not find price for selected platform tier and billing period.');
        setProcessing(null);
        return;
      }
      
      if (!selectedTestPlatformPrice.id) {
        alert('Error: Price ID is missing from platform price object.');
        console.error('Price object:', selectedTestPlatformPrice);
        console.error('Product prices:', selectedTestPlatformProduct?.prices);
        setProcessing(null);
        return;
      }
      
      // Validate price ID format (should start with 'price_')
      if (!selectedTestPlatformPrice.id.startsWith('price_')) {
        console.error('Invalid price ID format:', selectedTestPlatformPrice.id);
        alert(`Error: Invalid price ID format: ${selectedTestPlatformPrice.id}. Price IDs should start with "price_".`);
        setProcessing(null);
        return;
      }
      
      // Find the First Five product price from test products (backend includes it as $0 line item)
      const testFirstFiveProduct = testStripeProducts.find((product) => isFirstFiveProduct(product));

      const testFirstFivePrice = testFirstFiveProduct
        ? (() => {
            const freePrice = testFirstFiveProduct.prices?.find(p => p.unitAmount === 0);
            if (freePrice) return freePrice;
            return getPriceForBillingPeriod(testFirstFiveProduct, 'monthly');
          })()
        : null;

      console.log('Test Modal - First Five Product:', testFirstFiveProduct?.name);
      console.log('Test Modal - First Five Price ID:', testFirstFivePrice?.id);

      if (!testFirstFivePrice?.id) {
        console.error('Test Modal - Available products:', testStripeProducts.map(p => ({
          name: p.name,
          prices: p.prices?.map(pr => ({ id: pr.id, amount: pr.unitAmount }))
        })));
        alert('Error: Could not find First Five Disciplemakers product price in test mode. Make sure "Seat Packs 5 Free" or similar product exists in Stripe test mode.');
        setProcessing(null);
        return;
      }

      // Platform-only checkout — includes First Five as $0 line item (dual-sub architecture)
      const createCheckoutSession = httpsCallable(functions, 'createOrganizationCheckoutSession');
      
      const checkoutData = {
        orgId,
        platformTier: testSelectedPlatformTier,
        platformPriceId: selectedTestPlatformPrice.id,
        firstFivePriceId: testFirstFivePrice.id, // Required: backend includes First Five as $0 line item
        billingPeriod: testBillingPeriod,
        couponCode: testCouponCode.trim() || undefined,
      };
      
      console.log('Test Modal - Checkout Data (platform + first five):', JSON.stringify(checkoutData, null, 2));
      
      const result = await createCheckoutSession(checkoutData);
      
      if (result.data?.url) {
        // Redirect to Stripe Checkout (test mode)
        window.location.href = result.data.url;
      } else {
        alert('Error creating test checkout session');
      }
    } catch (error) {
      console.error('Error creating test checkout session:', error);
      alert(`Error: ${error.message || 'Failed to create test checkout session'}`);
    } finally {
      setProcessing(null);
    }
  };

  // Test subscription handler — Phase 2: Add/Upgrade seat pack (uses card on file, no Stripe redirect)
  const handleTestAddSeatPack = async () => {
    try {
      setProcessing('test-add-seatpack');
      if (!testSelectedDisciplemakerPack || testSelectedDisciplemakerPack === 'first-five') {
        alert('Please select a disciplemaker pack.');
        setProcessing(null);
        return;
      }

      // Get seat pack products from test products
      const testSeatPackProducts = testStripeProducts
        .filter((product) => {
          if (isArchivedProduct(product)) return false;
          if (isPlatformProduct(product)) return false;
          if (isFirstFiveProduct(product)) return false;
          if (!isSeatPackProduct(product)) return false;
          return true;
        })
        .sort(sortByPreferredOrder(seatPackOrder));

      const selectedTestDisciplemakerProduct = testSeatPackProducts.find(p => getSeatPackTier(p) === testSelectedDisciplemakerPack);
      const selectedTestDisciplemakerPrice = selectedTestDisciplemakerProduct
        ? getPriceForBillingPeriod(selectedTestDisciplemakerProduct, testBillingPeriod)
        : null;

      console.log('Test Modal - Seat Pack Product:', selectedTestDisciplemakerProduct?.name);
      console.log('Test Modal - Seat Pack Price:', selectedTestDisciplemakerPrice);

      if (!selectedTestDisciplemakerPrice?.id) {
        alert('Error: Could not find price for selected disciplemaker pack and billing period.');
        setProcessing(null);
        return;
      }

      // Confirm with the user — no card entry needed
      const packName = selectedTestDisciplemakerProduct?.name || testSelectedDisciplemakerPack;
      const priceLabel = selectedTestDisciplemakerPrice
        ? `${formatCurrency(selectedTestDisciplemakerPrice.unitAmount, selectedTestDisciplemakerPrice.currency)}${formatIntervalLabel(selectedTestDisciplemakerPrice)}`
        : '';
      const confirmMsg = `[TEST] Add "${packName}" for ${priceLabel}?\n\nYour card on file will be charged. This creates a separate subscription that can be managed independently from your platform subscription.`;

      if (!window.confirm(confirmMsg)) {
        setProcessing(null);
        return;
      }

      const addSeatPack = httpsCallable(functions, 'addSeatPackSubscription');
      const result = await addSeatPack({
        orgId,
        seatPackTier: testSelectedDisciplemakerPack,
        seatPackPriceId: selectedTestDisciplemakerPrice.id,
        billingPeriod: testBillingPeriod,
      });

      if (result.data?.subscriptionId) {
        alert(`[TEST] Seat pack added! ${result.data.disciplemakers} disciplemaker licenses now available.`);
        handleCloseTestModal();
        await loadOrganization();
        await loadMembers();
      } else {
        alert('Error adding seat pack');
      }
    } catch (error) {
      console.error('Error adding test seat pack:', error);
      alert(`Error: ${error.message || 'Failed to add seat pack'}`);
    } finally {
      setProcessing(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      setProcessing('billing');
      const createPortalSession = httpsCallable(functions, 'createOrganizationPortalSession');
      const result = await createPortalSession({ orgId });
      
      if (result.data?.url) {
        window.open(result.data.url, '_blank');
      } else {
        alert('Error creating billing portal session');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      alert('Error opening billing portal');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (typeof timestamp === 'number') {
      // Stripe timestamps are in seconds
      date = new Date(timestamp * 1000);
    } else {
      date = new Date(timestamp);
    }
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Calculate next invoice date based on billing cycle
  const getNextInvoiceDate = () => {
    // First try currentPeriodEnd from Stripe
    const periodEnd = license.currentPeriodEnd;
    if (periodEnd) {
      let endDate;
      if (periodEnd.toDate) {
        endDate = periodEnd.toDate();
      } else if (typeof periodEnd === 'number') {
        endDate = new Date(periodEnd * 1000);
      } else {
        endDate = new Date(periodEnd);
      }
      // If the end date is in the future (not same day as start), use it
      const now = new Date();
      if (!isNaN(endDate.getTime()) && endDate > now) {
        return endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      }
    }

    // Fallback: calculate from subscription start + billing cycle
    const startDate = license.subscriptionStartedAt;
    if (startDate) {
      let start;
      if (startDate.toDate) {
        start = startDate.toDate();
      } else {
        start = new Date(startDate);
      }
      if (!isNaN(start.getTime())) {
        const next = new Date(start);
        if (license.billingCycle === 'annual') {
          next.setFullYear(next.getFullYear() + 1);
        } else {
          next.setMonth(next.getMonth() + 1);
        }
        return next.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      }
    }
    return 'N/A';
  };

  const formatIntervalLabel = (price) => {
    const interval = price?.recurring?.interval;
    if (!interval) return '';
    return interval === 'month' ? '/mo' : interval === 'year' ? '/yr' : `/${interval}`;
  };

  // Helper to get price based on billing period
  const getPriceForBillingPeriod = (product, period) => {
    if (!product || !product.prices || product.prices.length === 0) return null;
    
    // Find price matching the billing period
    const matchingPrice = product.prices.find((p) => {
      const interval = p.recurring?.interval;
      if (period === 'annual') return interval === 'year';
      if (period === 'monthly') return interval === 'month';
      return false;
    });
    
    // Fallback to any active price if exact match not found
    return matchingPrice || product.prices.find((p) => p.active) || product.prices[0];
  };

  // Check if a product has a price for a specific billing period (no fallback)
  const hasExactPriceForPeriod = (product, period) => {
    if (!product || !product.prices || product.prices.length === 0) return false;
    return product.prices.some((p) => {
      const interval = p.recurring?.interval;
      if (period === 'annual') return interval === 'year';
      if (period === 'monthly') return interval === 'month';
      return false;
    });
  };

  // Get price for a specific period WITHOUT fallback (returns null if no exact match)
  const getExactPriceForPeriod = (product, period) => {
    if (!product || !product.prices || product.prices.length === 0) return null;
    return product.prices.find((p) => {
      const interval = p.recurring?.interval;
      if (period === 'annual') return interval === 'year';
      if (period === 'monthly') return interval === 'month';
      return false;
    }) || null;
  };

  // Detect which billing periods are available across a set of products
  const getAvailableBillingPeriods = (products) => {
    const hasMonthly = products.some(p => hasExactPriceForPeriod(p, 'monthly'));
    const hasAnnual = products.some(p => hasExactPriceForPeriod(p, 'annual'));
    return { hasMonthly, hasAnnual };
  };

  // Calculate savings percentage — only when both real monthly and annual prices exist
  const calculateSavings = (monthlyPrice, annualPrice) => {
    if (!monthlyPrice || !annualPrice || monthlyPrice <= 0 || annualPrice <= 0) return 0;
    const monthlyTotal = monthlyPrice * 12;
    const savings = monthlyTotal - annualPrice;
    return Math.round((savings / monthlyTotal) * 100);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">Organization not found</p>
        </div>
      </div>
    );
  }

  const license = organization?.license || {};
  const licenseCount = license.planId?.replace('organization-', '') || 'N/A';
  // Use disciplemakers terminology (preferred) with fallback to seats (legacy)
  const availableLicenses = license.disciplemakersAvailable ?? license.seatsAvailable ?? 0;
  const usedLicenses = license.disciplemakersUsed ?? license.seatsUsed ?? 0;
  const totalLicenses = license.disciplemakers ?? license.seats ?? 0;
  
  // Check for active subscription - check multiple conditions
  const hasSubscription = !!(
    license &&
    (license.status === 'active' ||
     license.status === 'trialing' ||
     license.platformSubscriptionId || // New: platform sub
     license.stripeSubscriptionId || // Legacy: combined sub
     license.stripeCustomerId) // If there's a Stripe customer ID, consider it active
  );
  
  // Debug logging (not in a hook to avoid hook count issues)
  if (organization && process.env.NODE_ENV === 'development') {
    console.log('Organization subscription check:', {
      hasLicense: !!license,
      licenseStatus: license?.status,
      stripeSubscriptionId: license?.stripeSubscriptionId,
      stripeCustomerId: license?.stripeCustomerId,
      hasSubscription,
      licenseKeys: Object.keys(license),
      fullLicense: license,
      // Check all possible subscription indicators
      hasStatus: !!license?.status,
      statusValue: license?.status,
      hasStripeSubscriptionId: !!license?.stripeSubscriptionId,
      hasStripeCustomerId: !!license?.stripeCustomerId,
      // Check if it's an empty object
      isEmptyObject: Object.keys(license).length === 0,
    });
  }
  
  // Format subscription start date
  const formatSubscriptionStartDate = () => {
    if (!license.subscriptionStartedAt) return 'N/A';
    const date = license.subscriptionStartedAt?.toDate ? license.subscriptionStartedAt.toDate() : new Date(license.subscriptionStartedAt);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // ── Product categorization ──
  // Use Firestore categories (admin-configured) when available, fall back to name heuristics.
  // Categories are only considered "configured" if at least one product has been assigned.
  // This prevents an empty/newly-saved config from hiding all products.

  const categoriesConfigured = productCategories && (
    (productCategories.platformProductIds?.length > 0) ||
    (productCategories.seatPackProductIds?.length > 0) ||
    !!productCategories.firstFiveProductId
  );

  const isFirstFiveProduct = (product) => {
    // Firestore category takes priority (only if actually configured)
    if (categoriesConfigured && productCategories?.firstFiveProductId === product.id) return true;
    if (categoriesConfigured) return false; // Categories configured but this product isn't First Five
    // Fallback: name heuristics
    const name = (product.name || '').toLowerCase();
    if (name.includes('first five') || name.includes('first 5') || name.includes('5 free')) return true;
    const productType = product.metadata?.productType?.toLowerCase();
    if (productType === 'first-five' || productType === 'first five') return true;
    return false;
  };

  const isSeatPackProduct = (product) => {
    // Firestore category takes priority (only if actually configured)
    if (categoriesConfigured && productCategories?.seatPackProductIds?.includes(product.id)) return true;
    if (categoriesConfigured && productCategories?.firstFiveProductId === product.id) return true; // First Five is a special seat pack
    if (categoriesConfigured) return false; // Categories configured but this product isn't a seat pack
    // Fallback: name/metadata heuristics
    const productType = product.metadata?.productType?.toLowerCase();
    if (productType === 'seat-pack' || productType === 'disciplemaker-pack' || productType === 'disciplemaker') return true;
    const name = (product.name || '').toLowerCase();
    if (name.includes('seat') || name.includes('pack') || name.includes('disciplemaker') || name.includes('mini')) return true;
    return false;
  };

  const isPlatformProduct = (product) => {
    // Firestore category takes priority (only if actually configured)
    if (categoriesConfigured && productCategories?.platformProductIds?.includes(product.id)) return true;
    if (categoriesConfigured) return false; // Categories configured but this product isn't platform
    // Fallback: name/metadata heuristics
    const productType = product.metadata?.productType?.toLowerCase();
    if (productType === 'platform') return true;
    const name = (product.name || '').toLowerCase();
    return name.includes('platform') || name.includes('pilot') || name.includes('standard') || name.includes('growing') || name.includes('large') || name.includes('multisite');
  };

  const isArchivedProduct = (product) => {
    return productCategories?.archivedProductIds?.includes(product.id) || false;
  };

  const platformOrder = ['starter', 'pilot', 'small', 'standard', 'growing', 'large', 'multisite'];
  const seatPackOrder = ['mini', 'starter', 'growth', 'active', 'city'];

  const sortByPreferredOrder = (order) => (a, b) => {
    const aName = (a.name || '').toLowerCase();
    const bName = (b.name || '').toLowerCase();
    const aIndex = order.findIndex((key) => aName.includes(key));
    const bIndex = order.findIndex((key) => bName.includes(key));
    if (aIndex !== -1 || bIndex !== -1) {
      return (aIndex === -1 ? order.length : aIndex) - (bIndex === -1 ? order.length : bIndex);
    }
    return aName.localeCompare(bName);
  };

  const platformProducts = stripeProducts
    .filter((product) => isPlatformProduct(product) && !isArchivedProduct(product))
    .sort(sortByPreferredOrder(platformOrder));

  // Get First Five product (automatically included)
  const firstFiveProduct = stripeProducts.find((product) => isFirstFiveProduct(product));
  const firstFivePrice = firstFiveProduct
    ? getPriceForBillingPeriod(firstFiveProduct, 'monthly') // First Five is always monthly
    : null;

  // Seat pack products — exclude platform, First Five, and archived
  const seatPackProducts = stripeProducts
    .filter((product) => {
      if (isArchivedProduct(product)) return false;
      if (isPlatformProduct(product)) return false;
      if (isFirstFiveProduct(product)) return false;
      if (!isSeatPackProduct(product)) return false;
      return true;
    })
    .sort(sortByPreferredOrder(seatPackOrder));

  // Helper to map platform product to tier
  const getPlatformTier = (product) => {
    // First check metadata
    const tierKey = product.metadata?.tierKey || product.metadata?.tier_id;
    if (tierKey) {
      if (['small-pilot', 'pilot', 'small', 'starter'].includes(tierKey.toLowerCase())) return 'small-pilot';
      if (tierKey.toLowerCase() === 'standard') return 'standard';
      if (tierKey.toLowerCase() === 'growing') return 'growing';
      if (['large-multisite', 'large', 'multisite'].includes(tierKey.toLowerCase())) return 'large-multisite';
    }
    
    // Then check name
    const name = (product.name || '').toLowerCase();
    if (name.includes('pilot') || name.includes('small') || name.includes('starter')) return 'small-pilot';
    if (name.includes('standard')) return 'standard';
    if (name.includes('growing')) return 'growing';
    if (name.includes('large') || name.includes('multisite')) return 'large-multisite';
    
    // If it's a platform product but no tier match, default to small-pilot for testing
    // This helps with products that don't have clear tier indicators
    if (isPlatformProduct(product)) {
      console.warn('Platform product without clear tier:', product.name, 'Defaulting to small-pilot');
      return 'small-pilot'; // Default fallback
    }
    
    return null;
  };

  // Icon component for organization tiers
  const TierIcon = ({ tier, isSelected, className = "w-8 h-8" }) => {
    const iconColor = isSelected ? '#2563EB' : '#6B7280';
    
    if (tier === 'small-pilot') {
      // Small building/house for starter/pilot
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke={iconColor} strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      );
    } else if (tier === 'standard') {
      // Medium building/church for standard
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke={iconColor} strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      );
    } else if (tier === 'growing') {
      // Upward trending/growth arrow for growing
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke={iconColor} strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      );
    } else if (tier === 'large-multisite') {
      // Multiple buildings for multisite/megachurch
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke={iconColor} strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 21V5a2 2 0 012-2h2a2 2 0 012 2v16" />
        </svg>
      );
    }
    return null;
  };

  // Helper to resolve the displayed license count for a product.
  // Priority: 1) Admin-configured seatCounts, 2) Stripe metadata, 3) name parsing
  const getProductLicenseCount = (product) => {
    // Priority 1: Admin-configured seat count
    const configuredCount = productCategories?.seatCounts?.[product.id];
    if (configuredCount && configuredCount > 0) return configuredCount;

    // Priority 2: Stripe product metadata
    if (product.metadata?.disciplemakers) {
      const parsed = parseInt(product.metadata.disciplemakers, 10);
      if (parsed > 0) return parsed;
    }
    if (product.metadata?.seats) {
      const parsed = parseInt(product.metadata.seats, 10);
      if (parsed > 0) return parsed;
    }

    // Priority 3: Extract from product name
    const matches = product.name?.match(/\d+/g);
    if (matches && matches.length > 0) {
      return Math.max(...matches.map(n => parseInt(n, 10)));
    }

    return null;
  };

  // Helper to map seat pack product to tier
  const getSeatPackTier = (product) => {
    console.log('getSeatPackTier - Product:', product.name, 'Metadata:', product.metadata);
    
    // First check metadata
    const tierKey = product.metadata?.tierKey || product.metadata?.tier_id;
    if (tierKey) {
      const tierLower = tierKey.toLowerCase();
      if (['starter', 'small-pilot'].includes(tierLower)) return 'starter';
      if (['growth', 'standard'].includes(tierLower)) return 'growth';
      if (['active', 'growing'].includes(tierLower)) return 'active';
      if (['city', 'large', 'multisite', 'large-multisite'].includes(tierLower)) return 'city';
      console.log('getSeatPackTier - Found tierKey but not recognized:', tierKey);
    }
    
    // Check metadata for disciplemaker count
    const disciplemakers = product.metadata?.disciplemakers 
      ? parseInt(product.metadata.disciplemakers, 10)
      : null;
    
    // Map disciplemaker counts to tiers (more flexible ranges)
    if (disciplemakers) {
      console.log('getSeatPackTier - Found disciplemakers in metadata:', disciplemakers);
      if (disciplemakers === 15 || (disciplemakers >= 5 && disciplemakers < 30)) return 'starter';
      if (disciplemakers === 50 || (disciplemakers >= 30 && disciplemakers < 75)) return 'growth';
      if (disciplemakers === 100 || (disciplemakers >= 75 && disciplemakers < 200)) return 'active';
      if (disciplemakers === 250 || disciplemakers >= 200) return 'city';
    }
    
    // Then check name
    const name = (product.name || '').toLowerCase();
    console.log('getSeatPackTier - Checking name:', name);
    
    if (name.includes('starter') && !name.includes('growth')) return 'starter';
    if (name.includes('growth')) return 'growth';
    if (name.includes('active')) return 'active';
    if (name.includes('city')) return 'city';
    
    // Extract ALL numbers from name and map to tier (check for multiple numbers)
    const numberMatches = name.match(/\d+/g);
    if (numberMatches && numberMatches.length > 0) {
      // Use the largest number found (in case there are multiple)
      const numbers = numberMatches.map(n => parseInt(n, 10));
      const maxNum = Math.max(...numbers);
      console.log('getSeatPackTier - Found numbers in name:', numbers, 'Using max:', maxNum);
      
      if (maxNum === 15 || (maxNum >= 5 && maxNum < 30)) return 'starter';
      if (maxNum === 50 || (maxNum >= 30 && maxNum < 75)) return 'growth';
      if (maxNum === 100 || (maxNum >= 75 && maxNum < 200)) return 'active';
      if (maxNum === 250 || maxNum >= 200) return 'city';
    }
    
    console.warn('getSeatPackTier - No tier found for product:', product.name);
    return null;
  };

  // Get selected platform product with price for current billing period
  const selectedPlatformProduct = platformProducts.find(p => getPlatformTier(p) === selectedPlatformTier);
  const selectedPlatformPrice = selectedPlatformProduct 
    ? getPriceForBillingPeriod(selectedPlatformProduct, billingPeriod)
    : null;
  const selectedPlatformMonthlyPrice = selectedPlatformProduct
    ? getExactPriceForPeriod(selectedPlatformProduct, 'monthly')
    : null;
  const selectedPlatformAnnualPrice = selectedPlatformProduct
    ? getExactPriceForPeriod(selectedPlatformProduct, 'annual')
    : null;

  // Get selected disciplemaker pack product with price for current billing period (only if not 'first-five')
  const selectedDisciplemakerProduct = selectedDisciplemakerPack && selectedDisciplemakerPack !== 'first-five'
    ? seatPackProducts.find(p => getSeatPackTier(p) === selectedDisciplemakerPack)
    : null;
  const selectedDisciplemakerPrice = selectedDisciplemakerProduct
    ? getPriceForBillingPeriod(selectedDisciplemakerProduct, billingPeriod)
    : null;
  const selectedDisciplemakerMonthlyPrice = selectedDisciplemakerProduct
    ? getExactPriceForPeriod(selectedDisciplemakerProduct, 'monthly')
    : null;
  const selectedDisciplemakerAnnualPrice = selectedDisciplemakerProduct
    ? getExactPriceForPeriod(selectedDisciplemakerProduct, 'annual')
    : null;

  // Calculate total based on current billing period
  const calculateTotal = () => {
    if (!selectedPlatformPrice) return null;
    // First Five is $0, so we only add the additional pack if one is selected (and not First Five)
    const packAmount = (selectedDisciplemakerPack && selectedDisciplemakerPack !== 'first-five' && selectedDisciplemakerPrice) 
      ? (selectedDisciplemakerPrice.unitAmount || 0) 
      : 0;
    const totalAmount = (selectedPlatformPrice.unitAmount || 0) + packAmount;
    
    // If annual, show monthly equivalent. If monthly, show annual equivalent for comparison
    if (billingPeriod === 'annual') {
      return {
        current: totalAmount,
        equivalent: Math.round(totalAmount / 12),
        period: 'annual',
        equivalentPeriod: 'monthly',
      };
    } else {
      return {
        current: totalAmount,
        equivalent: totalAmount * 12,
        period: 'monthly',
        equivalentPeriod: 'annual',
      };
    }
  };
  const total = calculateTotal();

  return (
    <div className={embedded ? 'w-full' : 'container mx-auto p-6'}>
      {!embedded && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate(-1)}
              className="text-blue-600 hover:text-blue-800"
            >
              ← Back
            </button>
            <button
              onClick={() => {
                loadOrganization();
                loadMembers();
                console.log('Refreshed subscription data');
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              title="Refresh subscription data"
            >
              🔄 Refresh
            </button>
          </div>
          <div className="flex items-center gap-4">
            {organization.logoUrl ? (
              <img
                src={organization.logoUrl}
                alt={organization.name}
                className="w-12 h-12 rounded-lg object-cover border border-gray-200"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-lg font-bold border border-blue-200">
                {(organization.name || 'O').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold">Subscription Management</h1>
              <p className="text-gray-600 text-sm">{organization.name}</p>
            </div>
          </div>
        </div>
      )}
      {embedded && (
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={() => {
              loadOrganization();
              loadMembers();
            }}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            title="Refresh subscription data"
          >
            🔄 Refresh
          </button>
        </div>
      )}

      {!hasSubscription ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-2">No Active Subscription</h2>
          <p className="text-gray-700 mb-4">
            This organization doesn't have an active subscription. Subscribe to get started.
          </p>
          
          {/* Debug info in development */}
          {process.env.NODE_ENV === 'development' && license && Object.keys(license).length > 0 && (
            <div className="mb-4 p-3 bg-gray-100 rounded text-xs">
              <p className="font-semibold mb-1">Debug Info (License object exists but subscription not detected):</p>
              <p>Status: {license.status || 'undefined'}</p>
              <p>Stripe Subscription ID: {license.stripeSubscriptionId || 'undefined'}</p>
              <p>Stripe Customer ID: {license.stripeCustomerId || 'undefined'}</p>
              <p className="text-gray-600 mt-2">
                If you just completed a payment, the webhook may still be processing. 
                Check Firebase Function logs or wait a few seconds and refresh.
              </p>
            </div>
          )}
          
          <div className="flex gap-4">
            <button
              onClick={handleUpgrade}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Subscribe Now
            </button>
            <button
              onClick={handleTestUpgrade}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 border-2 border-orange-700"
              title="Test subscription flow with test Stripe products"
            >
              🧪 TEST Subscribe
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveTab('subscription')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'subscription'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Subscription
                </span>
              </button>
              <button
                onClick={() => setActiveTab('licenses')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'licenses'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Licenses
                  <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                    {usedLicenses}/{totalLicenses}
                  </span>
                </span>
              </button>
              <button
                onClick={() => {
                  setActiveTab('billing');
                  if (billingHistory.length === 0 && !loadingBilling) {
                    loadBillingHistory();
                  }
                }}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'billing'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                  </svg>
                  Billing
                </span>
              </button>
            </nav>
          </div>

          {/* ===================== SUBSCRIPTION TAB ===================== */}
          {activeTab === 'subscription' && (
          <>
          {/* Early Adopter Badge */}
          {license.earlyAdopter && (
            <div className="mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                ⭐ Early Adopter
              </span>
            </div>
          )}

          {/* Platform Subscription Card */}
          <div className="bg-white border rounded-lg p-6 mb-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-semibold mb-1">Platform Subscription</h2>
                <div className="text-base font-medium text-gray-800">
                  {loadingProductNames ? 'Loading...' : (platformProductName || license.platformTier || 'Platform')}
                </div>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                (license.platformStatus || license.status) === 'active'
                  ? 'bg-green-100 text-green-800'
                  : (license.platformStatus || license.status) === 'canceled'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-blue-100 text-blue-800'
              }`}>
                {(license.platformStatus || license.status) === 'active' ? '● Active' : (license.platformStatus || license.status)}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-gray-500">Started</div>
                <div className="font-semibold">{formatSubscriptionStartDate()}</div>
              </div>
              <div>
                <div className="text-gray-500">Billing Cycle</div>
                <div className="font-semibold capitalize">{license.platformBillingCycle || license.billingCycle || 'N/A'}</div>
              </div>
              <div>
                <div className="text-gray-500">Next Invoice</div>
                <div className="font-semibold">{(() => {
                  const pEnd = license.platformCurrentPeriodEnd || license.currentPeriodEnd;
                  if (!pEnd) return 'N/A';
                  const d = pEnd.toDate ? pEnd.toDate() : new Date(typeof pEnd === 'number' ? pEnd * 1000 : pEnd);
                  return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                })()}</div>
              </div>
              <div>
                <div className="text-gray-500">Disciplemakers (free)</div>
                <div className="font-semibold text-green-600">5 included</div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t flex gap-3">
              <button
                onClick={() => handleUpgrade('platform')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Change Platform Tier
              </button>
            </div>
          </div>

          {/* Seat Pack Subscription Card */}
          <div className={`border rounded-lg p-6 mb-4 ${license.seatPackSubscriptionId ? 'bg-white' : 'bg-gray-50 border-dashed'}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-semibold mb-1">Disciplemaker Pack</h2>
                {license.seatPackSubscriptionId ? (
                  <div className="text-base font-medium text-gray-800">
                    {loadingProductNames ? 'Loading...' : (disciplemakerPackProductName || license.disciplemakerPackTier || license.seatPackTier || 'Pack')}
                  </div>
                ) : (
                  <div className="text-base text-gray-500">No seat pack — using 5 free disciplemakers</div>
                )}
              </div>
              {license.seatPackSubscriptionId ? (
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  (license.seatPackStatus || 'active') === 'active'
                    ? 'bg-green-100 text-green-800'
                    : (license.seatPackStatus) === 'canceled'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-blue-100 text-blue-800'
                }`}>
                  {(license.seatPackStatus || 'active') === 'active' ? '● Active' : license.seatPackStatus}
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                  Free Tier
                </span>
              )}
            </div>

            {license.seatPackSubscriptionId ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Billing Cycle</div>
                    <div className="font-semibold capitalize">{license.seatPackBillingCycle || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Next Invoice</div>
                    <div className="font-semibold">{(() => {
                      const spEnd = license.seatPackCurrentPeriodEnd;
                      if (!spEnd) return 'N/A';
                      const d = spEnd.toDate ? spEnd.toDate() : new Date(typeof spEnd === 'number' ? spEnd * 1000 : spEnd);
                      return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                    })()}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Total Disciplemakers</div>
                    <div className="font-semibold">{totalLicenses}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Available</div>
                    <div className="font-semibold text-green-600">{availableLicenses}</div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex gap-3">
                  <button
                    onClick={() => handleUpgrade('disciplemakers')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Upgrade Pack
                  </button>
                  <button
                    onClick={handleCancelSeatPack}
                    disabled={processing === 'cancel-seatpack'}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm disabled:opacity-50"
                  >
                    {processing === 'cancel-seatpack' ? 'Cancelling...' : 'Cancel Pack'}
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600 mb-3">
                  Need more than 5 disciplemakers? Add a pack — your card on file will be used, no extra checkout required.
                </p>
                <button
                  onClick={() => handleUpgrade('disciplemakers')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  + Add Disciplemaker Pack
                </button>
              </div>
            )}
          </div>

          {/* Note about independent billing */}
          {license.seatPackSubscriptionId && license.platformBillingCycle !== license.seatPackBillingCycle && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-sm text-blue-800">
              <strong>Independent billing cycles:</strong> Your platform is billed {license.platformBillingCycle || 'annually'} and your disciplemaker pack is billed {license.seatPackBillingCycle || 'monthly'}. Each can be managed separately.
            </div>
          )}
          </>
          )}

          {/* ===================== LICENSES TAB ===================== */}
          {activeTab === 'licenses' && (
          <>
            {/* License Usage Summary */}
            <div className="bg-white border rounded-lg p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h2 className="text-xl font-semibold">Disciplemaker Licenses</h2>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold text-lg text-blue-600">{usedLicenses}</span>
                    {pendingInvitations.length > 0 && (
                      <span className="text-yellow-600"> + {pendingInvitations.length} pending</span>
                    )}
                    {' '}of <span className="font-semibold">{totalLicenses}</span> used
                    {(() => {
                      const effectiveAvailable = Math.max(0, availableLicenses - pendingInvitations.length);
                      return effectiveAvailable > 0 ? (
                        <span className="ml-2 text-green-600">({effectiveAvailable} available)</span>
                      ) : null;
                    })()}
                  </div>
                  <button
                    onClick={() => navigate(`/organization/${orgId}/invite`)}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Invite Members
                  </button>
                </div>
              </div>

              {/* Progress bar — shows allocated (blue) + pending/held (yellow) + available (gray) */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2 flex overflow-hidden">
                {totalLicenses > 0 && (
                  <>
                    <div
                      className={`h-3 transition-all ${
                        availableLicenses === 0 && pendingInvitations.length === 0 ? 'bg-red-500' : 'bg-blue-600'
                      }`}
                      style={{ width: `${Math.min((usedLicenses / totalLicenses) * 100, 100)}%` }}
                    />
                    {pendingInvitations.length > 0 && (
                      <div
                        className="h-3 bg-yellow-400 transition-all"
                        style={{ width: `${Math.min((pendingInvitations.length / totalLicenses) * 100, 100 - (usedLicenses / totalLicenses) * 100)}%` }}
                      />
                    )}
                  </>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-4">
                {usedLicenses} allocated
                {pendingInvitations.length > 0 && ` · ${pendingInvitations.length} held (pending invites)`}
                {' '}· {Math.max(0, availableLicenses - pendingInvitations.length)} of {totalLicenses} available
              </p>

              {availableLicenses === 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-yellow-800">
                    All disciplemaker licenses are allocated. Upgrade your subscription to add more.
                  </p>
                  <button
                    onClick={handleUpgrade}
                    className="mt-2 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
                  >
                    Upgrade Subscription
                  </button>
                </div>
              )}
            </div>

            {/* Provisional License Banner */}
            {(() => {
              const provisionalCount = members.filter(m => m.licenseStatus === 'provisional').length;
              if (provisionalCount === 0) return null;
              const soonestExpiry = members
                .filter(m => m.licenseStatus === 'provisional' && m.licenseExpiresAt)
                .map(m => m.licenseExpiresAt?.toDate ? m.licenseExpiresAt.toDate() : new Date(m.licenseExpiresAt))
                .sort((a, b) => a - b)[0];
              const daysLeft = soonestExpiry ? Math.max(0, Math.ceil((soonestExpiry - Date.now()) / (1000 * 60 * 60 * 24))) : 0;
              return (
                <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <svg className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.068 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="font-semibold text-orange-900">{provisionalCount} member{provisionalCount > 1 ? 's' : ''} on provisional access</h4>
                    <p className="text-sm text-orange-700 mt-1">
                      {daysLeft > 0
                        ? `Earliest expiry in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Upgrade your seats to convert them to full licenses.`
                        : 'Some provisional licenses have expired. Upgrade to restore access.'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleUpgrade('disciplemakers')}
                    className="flex-shrink-0 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700"
                  >
                    Upgrade Now
                  </button>
                </div>
              );
            })()}

            {/* License Seats Grid */}
            <div className="bg-white border rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">License Seats</h3>
                <span className="text-xs text-gray-400 uppercase tracking-wider">
                  {usedLicenses} allocated · {availableLicenses} open
                </span>
              </div>

              <div className="space-y-2">
                {/* Creator/Admin — always seat #1, included free */}
                {(() => {
                  const creatorId = typeof organization?.createdBy === 'string'
                    ? organization.createdBy
                    : organization?.createdBy?.uid || organization?.createdBy?.id;
                  const creatorMember = members.find(m => m.userId === creatorId);
                  const creatorName = creatorMember?.userName || organization?.creatorName || 'Organization Creator';
                  const creatorEmail = creatorMember?.userEmail || '';

                  return (
                    <div className="flex items-center justify-between p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                          1
                        </div>
                        <div>
                          <div className="font-semibold text-blue-900">{creatorName}</div>
                          {creatorEmail && <div className="text-sm text-blue-700">{creatorEmail}</div>}
                          <div className="text-xs text-blue-600 font-medium">Creator · License included free</div>
                        </div>
                      </div>
                      <span className="text-blue-600 font-semibold text-sm flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        Included
                      </span>
                    </div>
                  );
                })()}

                {/* Allocated members (excluding creator) */}
                {(() => {
                  const creatorId = typeof organization?.createdBy === 'string'
                    ? organization.createdBy
                    : organization?.createdBy?.uid || organization?.createdBy?.id;

                  const allocatedMembers = members.filter(m =>
                    m.userId !== creatorId && m.licenseAllocated && m.licenseStatus !== 'provisional' && m.licenseStatus !== 'revoked'
                  );
                  const provisionalMembers = members.filter(m =>
                    m.userId !== creatorId && m.licenseStatus === 'provisional'
                  );
                  const revokedMembers = members.filter(m =>
                    m.userId !== creatorId && m.licenseStatus === 'revoked'
                  );
                  const unallocatedMembers = members.filter(m =>
                    m.userId !== creatorId && !m.licenseAllocated && m.licenseStatus !== 'revoked'
                  );

                  let seatNumber = 2; // Start after creator

                  return (
                    <>
                      {/* Allocated member seats */}
                      {allocatedMembers.map((member) => {
                        const num = seatNumber++;
                        return (
                          <div
                            key={member.userId}
                            className="flex items-center justify-between p-4 border border-green-200 rounded-lg bg-green-50"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">
                                {num}
                              </div>
                              <div>
                                <div className="font-semibold text-green-900">{member.userName}</div>
                                <div className="text-sm text-green-700">{member.userEmail}</div>
                                <div className="text-xs text-green-600">
                                  {member.role}
                                  {member.licenseAllocatedAt && ` · Allocated ${formatDate(member.licenseAllocatedAt)}`}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-green-600 text-sm font-medium">✓ Licensed</span>
                              <button
                                onClick={() => releaseLicense(member.userId)}
                                disabled={processing === member.userId}
                                className="px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                              >
                                {processing === member.userId ? '...' : 'Revoke'}
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Provisional members (grace period) */}
                      {provisionalMembers.map((member) => {
                        const num = seatNumber++;
                        const expiresAt = member.licenseExpiresAt?.toDate ? member.licenseExpiresAt.toDate() : member.licenseExpiresAt ? new Date(member.licenseExpiresAt) : null;
                        const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - Date.now()) / (1000 * 60 * 60 * 24))) : 0;
                        return (
                          <div
                            key={member.userId}
                            className="flex items-center justify-between p-4 border border-orange-200 rounded-lg bg-orange-50"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-orange-400 text-white flex items-center justify-center text-sm font-bold">
                                {num}
                              </div>
                              <div>
                                <div className="font-semibold text-orange-900">{member.userName}</div>
                                <div className="text-sm text-orange-700">{member.userEmail}</div>
                                <div className="text-xs text-orange-600">
                                  {member.role} · Provisional · {daysLeft > 0 ? `${daysLeft} days remaining` : 'Expired'}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-orange-600 text-xs font-medium px-2 py-0.5 bg-orange-100 rounded-full border border-orange-300">
                                Provisional
                              </span>
                              <button
                                onClick={() => releaseLicense(member.userId)}
                                disabled={processing === member.userId}
                                className="px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                              >
                                {processing === member.userId ? '...' : 'Revoke'}
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Unallocated members (can be assigned) */}
                      {unallocatedMembers.map((member) => {
                        const num = seatNumber++;
                        return (
                          <div
                            key={member.userId}
                            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-sm font-bold">
                                {num}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-800">{member.userName}</div>
                                <div className="text-sm text-gray-500">{member.userEmail}</div>
                                <div className="text-xs text-gray-400">{member.role} · No license</div>
                              </div>
                            </div>
                            <button
                              onClick={() => allocateLicense(member.userId)}
                              disabled={processing === member.userId || availableLicenses === 0}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed text-sm"
                            >
                              {processing === member.userId ? 'Allocating...' : 'Allocate'}
                            </button>
                          </div>
                        );
                      })}

                      {/* Revoked members (can be reinstated) */}
                      {revokedMembers.map((member) => {
                        const num = seatNumber++;
                        return (
                          <div
                            key={member.userId}
                            className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-red-300 text-red-800 flex items-center justify-center text-sm font-bold">
                                {num}
                              </div>
                              <div>
                                <div className="font-semibold text-red-900">{member.userName}</div>
                                <div className="text-sm text-red-700">{member.userEmail}</div>
                                <div className="text-xs text-red-600">{member.role} · License revoked</div>
                              </div>
                            </div>
                            <button
                              onClick={() => allocateLicense(member.userId)}
                              disabled={processing === member.userId}
                              className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                            >
                              {processing === member.userId ? '...' : 'Reinstate'}
                            </button>
                          </div>
                        );
                      })}

                      {/* Pending invitations — held seats */}
                      {pendingInvitations.map((invite) => {
                        const num = seatNumber++;
                        const inviteDate = invite.createdAt?.toDate ? invite.createdAt.toDate() : new Date(invite.createdAt || 0);
                        return (
                          <div
                            key={`invite-${invite.id}`}
                            className="flex items-center justify-between p-4 border border-yellow-200 rounded-lg bg-yellow-50"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-yellow-400 text-yellow-900 flex items-center justify-center text-sm font-bold">
                                {num}
                              </div>
                              <div>
                                <div className="font-semibold text-yellow-900">{invite.email}</div>
                                <div className="text-xs text-yellow-700">
                                  Invited {inviteDate.toLocaleDateString()} · Pending acceptance
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-yellow-600 text-xs font-medium px-2 py-0.5 bg-yellow-100 rounded-full border border-yellow-300">
                                Held
                              </span>
                              <button
                                onClick={() => revokeInvitation(invite.id)}
                                disabled={processing === `revoke-${invite.id}`}
                                className="px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                              >
                                {processing === `revoke-${invite.id}` ? '...' : 'Revoke'}
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Skeleton rows for remaining available seats */}
                      {(() => {
                        const filledSeats = 1 + allocatedMembers.length + provisionalMembers.length + unallocatedMembers.length + revokedMembers.length + pendingInvitations.length; // 1 for creator
                        const remainingSkeletons = Math.max(0, totalLicenses - filledSeats);
                        const skeletons = [];

                        for (let i = 0; i < remainingSkeletons; i++) {
                          const num = seatNumber++;
                          skeletons.push(
                            <div
                              key={`skeleton-${i}`}
                              className="flex items-center justify-between p-4 border border-dashed border-gray-200 rounded-lg bg-gray-50"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-400 flex items-center justify-center text-sm font-bold">
                                  {num}
                                </div>
                                <div>
                                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                                  <div className="h-3 w-48 bg-gray-100 rounded animate-pulse mt-1.5"></div>
                                </div>
                              </div>
                              <span className="text-xs text-gray-400 italic">Available</span>
                            </div>
                          );
                        }
                        return skeletons;
                      })()}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Allocation History */}
            <div className="bg-white border rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Allocation History</h3>
                <button
                  onClick={() => {
                    setShowHistory(!showHistory);
                    if (!showHistory) {
                      loadAllocationHistory();
                    }
                  }}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
                >
                  {showHistory ? 'Hide' : 'Show History'}
                </button>
              </div>

              {showHistory && (
                <div>
                  {loadingHistory ? (
                    <div className="text-center py-8">Loading history...</div>
                  ) : allocationHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No allocation history found</div>
                  ) : (
                    <div className="space-y-2">
                      {allocationHistory.map((entry) => (
                        <div
                          key={entry.id}
                          className={`p-3 border rounded text-sm ${
                            entry.action === 'allocated' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-medium">
                                {entry.action === 'allocated' ? '✓ Allocated' : '✗ Released'}
                              </span>
                              <span className="text-gray-700"> — {entry.userName}</span>
                              <div className="text-xs text-gray-500 mt-1">
                                {entry.action === 'allocated'
                                  ? `by ${entry.allocatedByName || 'Unknown'}`
                                  : `by ${entry.releasedByName || 'Unknown'}`}
                                {entry.timestamp && ` · ${formatDate(entry.timestamp)}`}
                              </div>
                              {entry.reason && (
                                <div className="text-xs text-gray-400 mt-0.5">
                                  Reason: {entry.reason}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
          )}

          {/* ===================== BILLING TAB ===================== */}
          {activeTab === 'billing' && (
          <>
            <div className="bg-white border rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold">Billing History</h2>
                  <p className="text-sm text-gray-500 mt-1">View all invoices and payments for this organization</p>
                </div>
                <button
                  onClick={loadBillingHistory}
                  disabled={loadingBilling}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm disabled:opacity-50"
                >
                  {loadingBilling ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>

              {billingError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {billingError}
                </div>
              )}

              {loadingBilling ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <p className="text-gray-500">Loading billing history...</p>
                </div>
              ) : billingHistory.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                  </svg>
                  <p className="text-gray-500">No invoices found</p>
                  <p className="text-sm text-gray-400 mt-1">Invoices will appear here after your first payment</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {/* Table */}
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Products</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Amount</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Status</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Receipt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {billingHistory.map((invoice) => (
                        <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                          {/* Date */}
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatInvoiceDate(invoice.created)}
                            </div>
                            {invoice.number && (
                              <div className="text-xs text-gray-400 font-mono mt-0.5">
                                {invoice.number}
                              </div>
                            )}
                          </td>

                          {/* Products */}
                          <td className="px-4 py-4">
                            {invoice.lineItems && invoice.lineItems.length > 0 ? (
                              <div className="space-y-1">
                                {invoice.lineItems.map((item, idx) => (
                                  <div key={idx} className="text-sm text-gray-700" title={item.description}>
                                    {item.description || 'Subscription'}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">Subscription</span>
                            )}
                          </td>

                          {/* Amount */}
                          <td className="px-4 py-4 text-right whitespace-nowrap">
                            <span className="text-sm font-semibold text-gray-900">
                              {formatCurrency(invoice.amountPaid || invoice.amountDue, invoice.currency)}
                            </span>
                            {invoice.paymentMethod?.last4 && (
                              <div className="text-xs text-gray-400 mt-0.5">
                                •••• {invoice.paymentMethod.last4}
                              </div>
                            )}
                          </td>

                          {/* Status */}
                          <td className="px-4 py-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadge(invoice.status)}`}>
                              {invoice.status === 'paid' ? '✓ Paid' : invoice.status}
                            </span>
                          </td>

                          {/* Receipt / Actions */}
                          <td className="px-4 py-4 text-right whitespace-nowrap">
                            <div className="flex justify-end gap-2">
                              {invoice.invoicePdf && (
                                <a
                                  href={invoice.invoicePdf}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
                                  title="Download PDF"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  PDF
                                </a>
                              )}
                              {invoice.hostedInvoiceUrl && (
                                <a
                                  href={invoice.hostedInvoiceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                                  title="View invoice"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  View
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Summary */}
                  <div className="mt-4 pt-4 border-t border-gray-200 px-4">
                    <span className="text-sm text-gray-500">
                      {billingHistory.length} invoice{billingHistory.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
          )}
        </>
      )}

      {/* Subscription Selection Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  {hasSubscription && currentStep === 'disciplemakers'
                    ? 'Add Disciplemaker Pack'
                    : hasSubscription
                      ? 'Change Platform Tier'
                      : 'Select Subscription Plan'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Billing Period Toggle */}
            {(() => {
              const relevantProducts = currentStep === 'platform' ? platformProducts : seatPackProducts;
              const { hasMonthly, hasAnnual } = getAvailableBillingPeriods(relevantProducts);
              const onlyOneOption = (hasMonthly && !hasAnnual) || (!hasMonthly && hasAnnual);
              return (
                <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
                  <div className="flex items-center justify-center gap-4">
                    <span className={`text-sm font-medium ${
                      !hasMonthly ? 'text-gray-300 line-through' :
                      billingPeriod === 'monthly' ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      Monthly
                    </span>
                    <button
                      onClick={() => {
                        if (onlyOneOption) return;
                        const next = billingPeriod === 'annual' ? 'monthly' : 'annual';
                        if ((next === 'monthly' && hasMonthly) || (next === 'annual' && hasAnnual)) {
                          setBillingPeriod(next);
                        }
                      }}
                      disabled={onlyOneOption}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                        onlyOneOption ? 'opacity-50 cursor-not-allowed' :
                        billingPeriod === 'annual' ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          billingPeriod === 'annual' ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${
                        !hasAnnual ? 'text-gray-300 line-through' :
                        billingPeriod === 'annual' ? 'text-gray-900' : 'text-gray-500'
                      }`}>
                        Annual
                      </span>
                      {hasMonthly && hasAnnual && (() => {
                        const maxSavings = relevantProducts.reduce((max, p) => {
                          const m = getExactPriceForPeriod(p, 'monthly');
                          const a = getExactPriceForPeriod(p, 'annual');
                          if (m && a) return Math.max(max, calculateSavings(m.unitAmount, a.unitAmount));
                          return max;
                        }, 0);
                        return maxSavings > 0 ? (
                          <span className="text-xs text-green-600 font-medium">
                            (Save {maxSavings}%)
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  {onlyOneOption && (
                    <p className="text-center text-xs text-gray-500 mt-2">
                      Only {hasAnnual ? 'annual' : 'monthly'} pricing is available for these products.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Step Indicator — only show two steps for new subscriptions */}
            {!hasSubscription ? (
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-center gap-2">
                  <div className="flex items-center gap-2 text-blue-600">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-blue-600 bg-blue-50">
                      1
                    </div>
                    <span className="font-medium">Select Platform</span>
                  </div>
                  <div className="w-8 h-0.5 bg-gray-300"></div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-gray-300">
                      2
                    </div>
                    <span className="font-medium text-sm">Stripe Checkout</span>
                  </div>
                </div>
                <p className="text-center text-xs text-gray-500 mt-2">
                  You can add a disciplemaker pack after checkout — uses your card on file (no re-entry).
                </p>
              </div>
            ) : (
              <div className="border-b border-gray-200 px-6 py-3">
                <p className="text-center text-sm text-gray-600">
                  {currentStep === 'platform'
                    ? 'Select a new platform tier. This will redirect to Stripe Checkout.'
                    : 'Choose a disciplemaker pack. Your card on file will be charged — no extra checkout needed.'}
                </p>
              </div>
            )}

            {/* Modal Body - Two Column Layout */}
            <div className="flex-1 flex overflow-hidden">
              {/* Main Content Area */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                {stripeLoading && (
                  <div className="bg-blue-50 border border-blue-100 text-blue-700 rounded-lg p-4 mb-6">
                    Loading Stripe products...
                  </div>
                )}
                {stripeError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
                    {stripeError}
                  </div>
                )}

                {currentStep === 'platform' ? (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {hasSubscription ? 'Change Platform Tier' : 'Platform Tier'}
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">Choose based on your church size</p>
                    {platformProducts.filter(p => hasExactPriceForPeriod(p, billingPeriod)).length === 0 && platformProducts.length > 0 && (
                      <div className="text-sm text-gray-500 mb-4">No platform products with {billingPeriod} pricing found. Try switching the billing period.</div>
                    )}
                    {platformProducts.length === 0 && (
                      <div className="text-sm text-gray-500">No platform products found in Stripe.</div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {platformProducts.filter(p => hasExactPriceForPeriod(p, billingPeriod)).map((product) => {
                        const tier = getPlatformTier(product);
                        const price = getPriceForBillingPeriod(product, billingPeriod);
                        const monthlyPrice = getExactPriceForPeriod(product, 'monthly');
                        const annualPrice = getExactPriceForPeriod(product, 'annual');
                        const selected = tier === selectedPlatformTier;
                        const savings = monthlyPrice && annualPrice ? calculateSavings(monthlyPrice.unitAmount, annualPrice.unitAmount) : 0;
                        
                        return (
                          <div
                            key={product.id}
                            onClick={() => {
                              if (tier) {
                                setSelectedPlatformTier(tier);
                              }
                            }}
                            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                              selected
                                ? 'border-blue-600 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                <div className="mt-1 flex-shrink-0">
                                  <TierIcon tier={tier} isSelected={selected} />
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900">{product.name}</h4>
                                <p className="text-sm text-gray-600 mt-1">{product.description || 'Platform access'}</p>
                                {price?.unitAmount !== null && (
                                  <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                                    <div className="inline-flex items-baseline gap-2 rounded-full bg-white border border-blue-100 px-3 py-1">
                                      <span className="text-lg font-semibold text-blue-700">
                                        {formatCurrency(price.unitAmount, price.currency)}
                                      </span>
                                      <span className="text-xs text-blue-600">{formatIntervalLabel(price)}</span>
                                    </div>
                                    {billingPeriod === 'annual' && savings > 0 && (
                                      <span className="text-xs text-green-600 font-medium">
                                        Save {savings}%
                                      </span>
                                    )}
                                  </div>
                                )}
                                </div>
                              </div>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                selected
                                  ? 'border-blue-600 bg-blue-600'
                                  : 'border-gray-300'
                              }`}>
                                {selected && (
                                  <div className="w-2 h-2 rounded-full bg-white"></div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Disciplemakers with Access</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Choose a disciplemaker pack to expand your team beyond the included 5.
                      </p>

                      {/* First Five — always included, shown as a static banner (not selectable) */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-green-800">First 5 Disciplemakers — Always Included</div>
                          <div className="text-xs text-green-700">Free with every platform subscription. The packs below add more seats on top of these 5.</div>
                        </div>
                        <span className="text-green-700 font-semibold text-sm">$0.00</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Additional Disciplemaker Packs */}
                        {seatPackProducts.filter(p => hasExactPriceForPeriod(p, billingPeriod)).length === 0 && (
                          <div className="text-sm text-gray-500 col-span-2">
                            {seatPackProducts.length === 0
                              ? 'No additional disciplemaker pack products found in Stripe.'
                              : `No packs with ${billingPeriod} pricing found. Try switching the billing period.`}
                          </div>
                        )}
                        {seatPackProducts.filter(p => hasExactPriceForPeriod(p, billingPeriod)).map((product) => {
                          const tier = getSeatPackTier(product);
                          const price = getPriceForBillingPeriod(product, billingPeriod);
                          const monthlyPrice = getExactPriceForPeriod(product, 'monthly');
                          const annualPrice = getExactPriceForPeriod(product, 'annual');
                          const selected = tier === selectedDisciplemakerPack;
                          const disciplemakers = getProductLicenseCount(product);
                          const savings = monthlyPrice && annualPrice ? calculateSavings(monthlyPrice.unitAmount, annualPrice.unitAmount) : 0;
                          
                          return (
                            <div
                              key={product.id}
                              onClick={() => tier && setSelectedDisciplemakerPack(tier)}
                              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                                selected
                                  ? 'border-blue-600 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold text-gray-900">{product.name}</h4>
                                  <p className="text-sm text-gray-600 mt-1">{product.description || 'Disciplemaker bundle'}</p>
                                  {disciplemakers && (
                                    <p className="text-xs text-gray-500 mt-1">{disciplemakers} disciplemakers included</p>
                                  )}
                                  {price?.unitAmount !== null && (
                                    <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                                      <div className="inline-flex items-baseline gap-2 rounded-full bg-white border border-blue-100 px-3 py-1">
                                        <span className="text-lg font-semibold text-blue-700">
                                          {formatCurrency(price.unitAmount, price.currency)}
                                        </span>
                                        <span className="text-xs text-blue-600">{formatIntervalLabel(price)}</span>
                                      </div>
                                      {billingPeriod === 'annual' && savings > 0 && (
                                        <span className="text-xs text-green-600 font-medium">
                                          Save {savings}%
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  selected
                                    ? 'border-blue-600 bg-blue-600'
                                    : 'border-gray-300'
                                }`}>
                                  {selected && (
                                    <div className="w-2 h-2 rounded-full bg-white"></div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Coupon Code (Optional) */}
                    <div className="mt-8">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Coupon Code (Optional)
                      </label>
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter coupon code (e.g., EARLY_ADOPTER_2025)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Early adopter discount available</p>
                    </div>
                  </>
                )}
              </div>

              {/* Cart Sidebar */}
              <div className="w-80 border-l border-gray-200 bg-gray-50 p-6 overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {currentStep === 'platform' ? 'Your Selection' : 'Adding Seat Pack'}
                </h3>

                {currentStep === 'platform' ? (
                  <>
                    {/* Platform selection cart */}
                    {selectedPlatformProduct ? (
                      <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200 relative">
                        <button
                          onClick={() => setSelectedPlatformTier(null)}
                          className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-xl leading-none"
                          title="Remove"
                        >
                          ×
                        </button>
                        <div className="text-xs text-gray-500 mb-1">Platform Tier</div>
                        <div className="font-semibold text-gray-900 pr-6">{selectedPlatformProduct.name}</div>
                        <div className="text-sm text-gray-600 mt-1">{selectedPlatformProduct.description || 'Platform access'}</div>
                        {selectedPlatformPrice && (
                          <div className="text-lg font-semibold text-blue-600 mt-2">
                            {formatCurrency(selectedPlatformPrice.unitAmount, selectedPlatformPrice.currency)}
                            {formatIntervalLabel(selectedPlatformPrice)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white rounded-lg p-8 border border-gray-200 text-center">
                        <div className="text-gray-400 mb-2">🛒</div>
                        <div className="text-sm text-gray-500">Select a platform tier to continue</div>
                      </div>
                    )}

                    {/* First Five always included */}
                    {selectedPlatformProduct && firstFiveProduct && (
                      <div className="bg-green-50 rounded-lg p-3 mb-4 border border-green-200">
                        <div className="text-xs text-green-700 font-medium">✓ Included Free</div>
                        <div className="text-sm font-semibold text-green-800 mt-1">First 5 Disciplemakers</div>
                        <div className="text-xs text-green-600">$0.00 — always included with platform</div>
                      </div>
                    )}

                    {/* Note about adding seat pack later */}
                    {selectedPlatformProduct && (
                      <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700 border border-blue-200">
                        After checkout, you can add a disciplemaker pack using your card on file — no re-entry needed.
                      </div>
                    )}

                    {selectedPlatformPrice && (
                      <div className="bg-white rounded-lg p-4 border-2 border-blue-200 mt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">
                            {billingPeriod === 'annual' ? 'Annual' : 'Monthly'} Total
                          </span>
                          <span className="text-xl font-bold text-blue-600">
                            {formatCurrency(selectedPlatformPrice.unitAmount, selectedPlatformPrice.currency)}
                          </span>
                        </div>
                        {billingPeriod === 'annual' && selectedPlatformMonthlyPrice && selectedPlatformAnnualPrice && (
                          <div className="text-xs text-green-600 font-medium text-right mt-1">
                            Save {calculateSavings(selectedPlatformMonthlyPrice.unitAmount, selectedPlatformAnnualPrice.unitAmount)}% vs monthly
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Seat pack selection cart */}
                    {selectedDisciplemakerProduct && selectedDisciplemakerPack !== 'first-five' ? (
                      <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200 relative">
                        <button
                          onClick={() => setSelectedDisciplemakerPack('first-five')}
                          className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-xl leading-none"
                          title="Remove"
                        >
                          ×
                        </button>
                        <div className="text-xs text-gray-500 mb-1">Disciplemaker Pack</div>
                        <div className="font-semibold text-gray-900 pr-6">{selectedDisciplemakerProduct.name}</div>
                        {selectedDisciplemakerPrice && (
                          <div className="text-lg font-semibold text-blue-600 mt-2">
                            {formatCurrency(selectedDisciplemakerPrice.unitAmount, selectedDisciplemakerPrice.currency)}
                            {formatIntervalLabel(selectedDisciplemakerPrice)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white rounded-lg p-6 border border-gray-200 text-center">
                        <div className="text-sm text-gray-500">Select a pack to add more disciplemakers beyond the free 5.</div>
                      </div>
                    )}

                    <div className="bg-yellow-50 rounded-lg p-3 text-xs text-yellow-800 border border-yellow-200 mt-4">
                      <strong>Card on file:</strong> This will create a separate subscription using your existing payment method. No extra checkout required.
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center">
              {currentStep === 'disciplemakers' && !hasSubscription && (
                <button
                  onClick={() => setCurrentStep('platform')}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  disabled={!!processing}
                >
                  ← Back
                </button>
              )}
              <div className="flex gap-4 ml-auto">
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={!!processing}
                >
                  Cancel
                </button>
                {currentStep === 'platform' ? (
                  <button
                    onClick={() => {
                      if (!hasSubscription) {
                        // New sub: platform checkout goes to Stripe
                        handleSubscribe();
                      } else {
                        // Existing sub: just changing platform tier via Stripe Checkout
                        handleSubscribe();
                      }
                    }}
                    disabled={processing === 'subscribe' || !selectedPlatformTier}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing === 'subscribe' ? 'Processing...' : 'Continue to Checkout'}
                  </button>
                ) : (
                  <button
                    onClick={handleAddSeatPack}
                    disabled={processing === 'add-seatpack' || !selectedDisciplemakerPack || selectedDisciplemakerPack === 'first-five'}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing === 'add-seatpack' ? 'Processing...' : 'Confirm — Use Card on File'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TEST Subscription Selection Modal */}
      {showTestSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4 bg-orange-50">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {hasSubscription && testCurrentStep === 'disciplemakers'
                      ? '🧪 Add Disciplemaker Pack (TEST)'
                      : hasSubscription
                        ? '🧪 Change Platform Tier (TEST)'
                        : '🧪 TEST Subscription Plan'}
                  </h2>
                  <p className="text-sm text-orange-700 mt-1">
                    {hasSubscription && testCurrentStep === 'disciplemakers'
                      ? 'Uses card on file — no extra checkout needed'
                      : 'Using dynamically loaded test products from Stripe'}
                  </p>
                </div>
                <button
                  onClick={handleCloseTestModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Billing Period Toggle */}
            {(() => {
              const relevantTestProducts = testCurrentStep === 'platform'
                ? testStripeProducts.filter(p => isPlatformProduct(p) && !isArchivedProduct(p))
                : testStripeProducts.filter(p => isSeatPackProduct(p) && !isPlatformProduct(p) && !isFirstFiveProduct(p) && !isArchivedProduct(p));
              const { hasMonthly: testHasMonthly, hasAnnual: testHasAnnual } = getAvailableBillingPeriods(relevantTestProducts);
              const testOnlyOneOption = (testHasMonthly && !testHasAnnual) || (!testHasMonthly && testHasAnnual);
              return (
                <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
                  <div className="flex items-center justify-center gap-4">
                    <span className={`text-sm font-medium ${
                      !testHasMonthly ? 'text-gray-300 line-through' :
                      testBillingPeriod === 'monthly' ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      Monthly
                    </span>
                    <button
                      onClick={() => {
                        if (testOnlyOneOption) return;
                        const next = testBillingPeriod === 'annual' ? 'monthly' : 'annual';
                        if ((next === 'monthly' && testHasMonthly) || (next === 'annual' && testHasAnnual)) {
                          setTestBillingPeriod(next);
                        }
                      }}
                      disabled={testOnlyOneOption}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2 ${
                        testOnlyOneOption ? 'opacity-50 cursor-not-allowed' :
                        testBillingPeriod === 'annual' ? 'bg-orange-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          testBillingPeriod === 'annual' ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${
                        !testHasAnnual ? 'text-gray-300 line-through' :
                        testBillingPeriod === 'annual' ? 'text-gray-900' : 'text-gray-500'
                      }`}>
                        Annual
                      </span>
                      {testHasMonthly && testHasAnnual && (() => {
                        const maxTestSavings = relevantTestProducts.reduce((max, p) => {
                          const m = getExactPriceForPeriod(p, 'monthly');
                          const a = getExactPriceForPeriod(p, 'annual');
                          if (m && a) return Math.max(max, calculateSavings(m.unitAmount, a.unitAmount));
                          return max;
                        }, 0);
                        return maxTestSavings > 0 ? (
                          <span className="text-xs text-green-600 font-medium">
                            (Save {maxTestSavings}%)
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  {testOnlyOneOption && (
                    <p className="text-center text-xs text-gray-500 mt-2">
                      Only {testHasAnnual ? 'annual' : 'monthly'} pricing is available for these products.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Step Indicator — dual subscription flow */}
            {!hasSubscription ? (
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-center gap-2">
                  <div className="flex items-center gap-2 text-orange-600">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-orange-600 bg-orange-50">
                      1
                    </div>
                    <span className="font-medium">Select Platform</span>
                  </div>
                  <div className="w-8 h-0.5 bg-gray-300"></div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-gray-300">
                      2
                    </div>
                    <span className="font-medium text-sm">Stripe Checkout</span>
                  </div>
                </div>
                <p className="text-center text-xs text-gray-500 mt-2">
                  You can add a disciplemaker pack after checkout — uses your card on file (no re-entry).
                </p>
              </div>
            ) : (
              <div className="border-b border-gray-200 px-6 py-3">
                <p className="text-center text-sm text-gray-600">
                  {testCurrentStep === 'platform'
                    ? 'Select a new platform tier. This will redirect to Stripe Checkout.'
                    : 'Choose a disciplemaker pack. Your card on file will be charged — no extra checkout needed.'}
                </p>
              </div>
            )}

            {/* Modal Body - Two Column Layout */}
            <div className="flex-1 flex overflow-hidden">
              {/* Main Content Area */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                {testStripeLoading && (
                  <div className="bg-orange-50 border border-orange-100 text-orange-700 rounded-lg p-4 mb-6">
                    Loading Stripe test products...
                  </div>
                )}
                {testStripeError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
                    {testStripeError}
                  </div>
                )}

                {/* Dynamically Loaded Test Products */}
                {testCurrentStep === 'platform' ? (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Platform Tier (TEST MODE)</h3>
                    <p className="text-sm text-gray-600 mb-6">Choose based on your church size</p>
                    {(() => {
                      const testPlatformProducts = testStripeProducts
                        .filter((product) => isPlatformProduct(product) && !isArchivedProduct(product))
                        .sort(sortByPreferredOrder(platformOrder));
                      
                      // Filter to only products with prices for the selected billing period
                      const filteredTestPlatformProducts = testPlatformProducts.filter(p => hasExactPriceForPeriod(p, testBillingPeriod));
                      
                      if (testPlatformProducts.length === 0 && !testStripeLoading) {
                        return (
                          <div className="text-sm text-gray-500">
                            No platform products found in Stripe test mode. Make sure you're logged in as a Super Admin and products are created in Stripe.
                          </div>
                        );
                      }

                      if (filteredTestPlatformProducts.length === 0 && testPlatformProducts.length > 0) {
                        return (
                          <div className="text-sm text-gray-500">
                            No platform products with {testBillingPeriod} pricing found. Try switching the billing period.
                          </div>
                        );
                      }
                      
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {filteredTestPlatformProducts.map((product) => {
                            const tier = getPlatformTier(product);
                            const price = getPriceForBillingPeriod(product, testBillingPeriod);
                            const monthlyPrice = getExactPriceForPeriod(product, 'monthly');
                            const annualPrice = getExactPriceForPeriod(product, 'annual');
                            const selected = tier === testSelectedPlatformTier;
                            const savings = monthlyPrice && annualPrice ? calculateSavings(monthlyPrice.unitAmount, annualPrice.unitAmount) : 0;
                            
                            return (
                              <div
                                key={product.id}
                                onClick={() => {
                                  if (tier) {
                                    console.log('Test Modal - Setting platform tier:', tier, 'from product:', product.name);
                                    setTestSelectedPlatformTier(tier);
                                    // Don't auto-advance - let user click Continue button
                                  } else {
                                    console.warn('Test Modal - No tier found for product:', product.name, product);
                                  }
                                }}
                                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                                  selected
                                    ? 'border-orange-600 bg-orange-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-3 flex-1">
                                    <div className="mt-1 flex-shrink-0">
                                      <TierIcon tier={tier} isSelected={selected} />
                                    </div>
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-gray-900">{product.name}</h4>
                                      <p className="text-sm text-gray-600 mt-1">{product.description || 'Platform access'}</p>
                                      {price?.unitAmount !== null && (
                                        <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                                          <div className="inline-flex items-baseline gap-2 rounded-full bg-white border border-orange-100 px-3 py-1">
                                            <span className="text-lg font-semibold text-orange-700">
                                              {formatCurrency(price.unitAmount, price.currency)}
                                            </span>
                                            <span className="text-xs text-orange-600">{formatIntervalLabel(price)}</span>
                                          </div>
                                          {testBillingPeriod === 'annual' && savings > 0 && (
                                            <span className="text-xs text-green-600 font-medium">
                                              Save {savings}%
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                    selected
                                      ? 'border-orange-600 bg-orange-600'
                                      : 'border-gray-300'
                                  }`}>
                                    {selected && (
                                      <div className="w-2 h-2 rounded-full bg-white"></div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Disciplemakers with Access (TEST MODE)</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Choose a disciplemaker pack to expand your team beyond the included 5.
                      </p>

                      {/* First Five — always included, shown as a static banner (not selectable) */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-green-800">First 5 Disciplemakers — Always Included</div>
                          <div className="text-xs text-green-700">Free with every platform subscription. The packs below add more seats on top of these 5.</div>
                        </div>
                        <span className="text-green-700 font-semibold text-sm">$0.00</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Additional Disciplemaker Packs */}
                        {(() => {
                          const testSeatPackProducts = testStripeProducts
                            .filter((product) => {
                              if (isArchivedProduct(product)) return false;
                              if (isPlatformProduct(product)) return false;
                              if (isFirstFiveProduct(product)) return false;
                              if (!isSeatPackProduct(product)) return false;
                              return true;
                            })
                            .sort(sortByPreferredOrder(seatPackOrder));
                          
                          // Debug: Log all products and filtered results
                          console.log('Test Modal - All products:', testStripeProducts.map(p => ({ 
                            name: p.name, 
                            metadata: p.metadata,
                            isSeatPack: isSeatPackProduct(p)
                          })));
                          console.log('Test Modal - Filtered seat pack products:', testSeatPackProducts.map(p => ({
                            name: p.name,
                            tier: getSeatPackTier(p)
                          })));
                          
                          // Filter to only products with prices for the selected billing period
                          const filteredTestSeatPacks = testSeatPackProducts.filter(p => hasExactPriceForPeriod(p, testBillingPeriod));
                          
                          if (testSeatPackProducts.length === 0 && !testStripeLoading) {
                            return (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <p className="text-sm text-yellow-800 mb-2">
                                  <strong>No additional disciplemaker pack products found.</strong>
                                </p>
                                <p className="text-xs text-yellow-700">
                                  Products are identified by name containing "disciplemaker", "pack", "seat", or metadata productType. 
                                  Check browser console for product details.
                                </p>
                              </div>
                            );
                          }

                          if (filteredTestSeatPacks.length === 0 && testSeatPackProducts.length > 0) {
                            return (
                              <div className="text-sm text-gray-500">
                                No packs with {testBillingPeriod} pricing found. Try switching the billing period.
                              </div>
                            );
                          }
                          
                          return filteredTestSeatPacks.map((product) => {
                            const tier = getSeatPackTier(product);
                            const price = getPriceForBillingPeriod(product, testBillingPeriod);
                            const monthlyPrice = getExactPriceForPeriod(product, 'monthly');
                            const annualPrice = getExactPriceForPeriod(product, 'annual');
                            const selected = tier === testSelectedDisciplemakerPack;
                            const disciplemakers = getProductLicenseCount(product);
                            const savings = monthlyPrice && annualPrice ? calculateSavings(monthlyPrice.unitAmount, annualPrice.unitAmount) : 0;
                            
                            // If no tier found, try to infer from disciplemaker count
                            let finalTier = tier;
                            if (!finalTier && disciplemakers) {
                              if (disciplemakers === 15 || (disciplemakers >= 5 && disciplemakers < 30)) finalTier = 'starter';
                              else if (disciplemakers === 50 || (disciplemakers >= 30 && disciplemakers < 75)) finalTier = 'growth';
                              else if (disciplemakers === 100 || (disciplemakers >= 75 && disciplemakers < 200)) finalTier = 'active';
                              else if (disciplemakers === 250 || disciplemakers >= 200) finalTier = 'city';
                              if (finalTier) {
                                console.log('Test Modal - Inferred tier:', finalTier, 'from disciplemaker count:', disciplemakers, 'for product:', product.name);
                              }
                            }
                            
                            return (
                              <div
                                key={product.id}
                                onClick={() => {
                                  if (finalTier) {
                                    console.log('Test Modal - Setting disciplemaker pack tier:', finalTier, 'from product:', product.name);
                                    setTestSelectedDisciplemakerPack(finalTier);
                                  } else {
                                    console.warn('Test Modal - No tier found for seat pack product:', product.name, product);
                                    alert(`Unable to determine tier for "${product.name}". Please check product metadata or name.`);
                                  }
                                }}
                                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                                  selected
                                    ? 'border-orange-600 bg-orange-50'
                                    : finalTier
                                      ? 'border-gray-200 hover:border-gray-300'
                                      : 'border-red-200 bg-red-50 opacity-50'
                                }`}
                                title={!finalTier ? 'Unable to determine tier - check console' : ''}
                              >
                                <div className="flex items-start justify-between">
                                  <div>
                                    <h4 className="font-semibold text-gray-900">{product.name}</h4>
                                    <p className="text-sm text-gray-600 mt-1">{product.description || 'Disciplemaker bundle'}</p>
                                    {disciplemakers && (
                                      <p className="text-xs text-gray-500 mt-1">{disciplemakers} disciplemakers included</p>
                                    )}
                                    {price?.unitAmount !== null && (
                                      <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                                        <div className="inline-flex items-baseline gap-2 rounded-full bg-white border border-orange-100 px-3 py-1">
                                          <span className="text-lg font-semibold text-orange-700">
                                            {formatCurrency(price.unitAmount, price.currency)}
                                          </span>
                                          <span className="text-xs text-orange-600">{formatIntervalLabel(price)}</span>
                                        </div>
                                        {testBillingPeriod === 'annual' && savings > 0 && (
                                          <span className="text-xs text-green-600 font-medium">
                                            Save {savings}%
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                    selected
                                      ? 'border-orange-600 bg-orange-600'
                                      : 'border-gray-300'
                                  }`}>
                                    {selected && (
                                      <div className="w-2 h-2 rounded-full bg-white"></div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>

                    {/* Coupon Code (Optional) */}
                    <div className="mt-8">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Coupon Code (Optional)
                      </label>
                      <input
                        type="text"
                        value={testCouponCode}
                        onChange={(e) => setTestCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter coupon code (e.g., EARLY_ADOPTER_2025)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Early adopter discount available</p>
                    </div>
                  </>
                )}
              </div>

              {/* Cart Sidebar — dual subscription flow */}
              <div className="w-80 border-l border-gray-200 bg-gray-50 p-6 overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {testCurrentStep === 'platform' ? 'Your Selection (TEST)' : 'Adding Seat Pack (TEST)'}
                </h3>

                {testCurrentStep === 'platform' ? (
                  <>
                    {/* Platform selection cart */}
                    {(() => {
                      const testPlatformProducts = testStripeProducts
                        .filter((product) => isPlatformProduct(product) && !isArchivedProduct(product))
                        .sort(sortByPreferredOrder(platformOrder));
                      const selectedTestPlatformProduct = testPlatformProducts.find(p => getPlatformTier(p) === testSelectedPlatformTier);
                      const selectedTestPlatformPrice = selectedTestPlatformProduct 
                        ? getPriceForBillingPeriod(selectedTestPlatformProduct, testBillingPeriod)
                        : null;
                      // Look for First Five product
                      const testFirstFiveProduct = testStripeProducts.find((product) => isFirstFiveProduct(product));
                      
                      return (
                        <>
                          {selectedTestPlatformProduct ? (
                            <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200 relative">
                              <button
                                onClick={() => setTestSelectedPlatformTier(null)}
                                className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-xl leading-none"
                                title="Remove"
                              >
                                ×
                              </button>
                              <div className="text-xs text-gray-500 mb-1">Platform Tier</div>
                              <div className="font-semibold text-gray-900 pr-6">{selectedTestPlatformProduct.name}</div>
                              <div className="text-sm text-gray-600 mt-1">{selectedTestPlatformProduct.description || 'Platform access'}</div>
                              {selectedTestPlatformPrice && (
                                <div className="text-lg font-semibold text-orange-600 mt-2">
                                  {formatCurrency(selectedTestPlatformPrice.unitAmount, selectedTestPlatformPrice.currency)}
                                  {formatIntervalLabel(selectedTestPlatformPrice)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="bg-white rounded-lg p-8 border border-gray-200 text-center">
                              <div className="text-gray-400 mb-2">🛒</div>
                              <div className="text-sm text-gray-500">Select a platform tier to continue</div>
                            </div>
                          )}

                          {/* First Five always included */}
                          {selectedTestPlatformProduct && testFirstFiveProduct && (
                            <div className="bg-green-50 rounded-lg p-3 mb-4 border border-green-200">
                              <div className="text-xs text-green-700 font-medium">✓ Included Free</div>
                              <div className="text-sm font-semibold text-green-800 mt-1">First 5 Disciplemakers</div>
                              <div className="text-xs text-green-600">$0.00 — always included with platform</div>
                            </div>
                          )}

                          {/* Note about adding seat pack later */}
                          {selectedTestPlatformProduct && (
                            <div className="bg-orange-50 rounded-lg p-3 text-xs text-orange-700 border border-orange-200">
                              After checkout, you can add a disciplemaker pack using your card on file — no re-entry needed.
                            </div>
                          )}

                          {selectedTestPlatformPrice && (
                            <div className="bg-white rounded-lg p-4 border-2 border-orange-200 mt-4">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">
                                  {testBillingPeriod === 'annual' ? 'Annual' : 'Monthly'} Total (TEST)
                                </span>
                                <span className="text-xl font-bold text-orange-600">
                                  {formatCurrency(selectedTestPlatformPrice.unitAmount, selectedTestPlatformPrice.currency)}
                                </span>
                              </div>
                              {testBillingPeriod === 'annual' && (() => {
                                const testMonthly = selectedTestPlatformProduct ? getExactPriceForPeriod(selectedTestPlatformProduct, 'monthly') : null;
                                const testAnnual = selectedTestPlatformProduct ? getExactPriceForPeriod(selectedTestPlatformProduct, 'annual') : null;
                                const pctSaved = testMonthly && testAnnual ? calculateSavings(testMonthly.unitAmount, testAnnual.unitAmount) : 0;
                                return pctSaved > 0 ? (
                                  <div className="text-xs text-green-600 font-medium text-right mt-1">Save {pctSaved}% vs monthly</div>
                                ) : null;
                              })()}
                              <div className="text-xs text-orange-600 font-medium text-right mt-2">
                                🧪 Test Mode - Use test card: 4242 4242 4242 4242
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    {/* Seat pack selection cart */}
                    {(() => {
                      const testSeatPackProducts = testStripeProducts
                        .filter((product) => {
                          if (isArchivedProduct(product)) return false;
                          if (isPlatformProduct(product)) return false;
                          if (isFirstFiveProduct(product)) return false;
                          if (!isSeatPackProduct(product)) return false;
                          return true;
                        })
                        .sort(sortByPreferredOrder(seatPackOrder));
                      const selectedTestDisciplemakerProduct = testSelectedDisciplemakerPack && testSelectedDisciplemakerPack !== 'first-five'
                        ? testSeatPackProducts.find(p => getSeatPackTier(p) === testSelectedDisciplemakerPack)
                        : null;
                      const selectedTestDisciplemakerPrice = selectedTestDisciplemakerProduct
                        ? getPriceForBillingPeriod(selectedTestDisciplemakerProduct, testBillingPeriod)
                        : null;

                      return (
                        <>
                          {selectedTestDisciplemakerProduct && testSelectedDisciplemakerPack !== 'first-five' ? (
                            <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200 relative">
                              <button
                                onClick={() => setTestSelectedDisciplemakerPack('first-five')}
                                className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-xl leading-none"
                                title="Remove"
                              >
                                ×
                              </button>
                              <div className="text-xs text-gray-500 mb-1">Disciplemaker Pack</div>
                              <div className="font-semibold text-gray-900 pr-6">{selectedTestDisciplemakerProduct.name}</div>
                              {selectedTestDisciplemakerPrice && (
                                <div className="text-lg font-semibold text-orange-600 mt-2">
                                  {formatCurrency(selectedTestDisciplemakerPrice.unitAmount, selectedTestDisciplemakerPrice.currency)}
                                  {formatIntervalLabel(selectedTestDisciplemakerPrice)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="bg-white rounded-lg p-6 border border-gray-200 text-center">
                              <div className="text-sm text-gray-500">Select a pack to add more disciplemakers beyond the free 5.</div>
                            </div>
                          )}

                          <div className="bg-yellow-50 rounded-lg p-3 text-xs text-yellow-800 border border-yellow-200 mt-4">
                            <strong>Card on file:</strong> This will create a separate subscription using your existing payment method. No extra checkout required.
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer — dual subscription actions */}
            <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center bg-white">
              {testCurrentStep === 'disciplemakers' && !hasSubscription && (
                <button
                  onClick={() => setTestCurrentStep('platform')}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  disabled={!!processing}
                >
                  ← Back
                </button>
              )}
              <div className="flex gap-4 ml-auto">
                <button
                  onClick={handleCloseTestModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={!!processing}
                >
                  Cancel
                </button>
                {testCurrentStep === 'platform' ? (
                  <button
                    onClick={() => {
                      console.log('Test Modal - Platform checkout. Tier:', testSelectedPlatformTier);
                      if (!hasSubscription) {
                        // New sub: platform checkout goes to Stripe
                        handleTestSubscribe();
                      } else {
                        // Existing sub: changing platform tier via Stripe Checkout
                        handleTestSubscribe();
                      }
                    }}
                    disabled={processing === 'test-subscribe' || !testSelectedPlatformTier}
                    className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {processing === 'test-subscribe' ? 'Processing...' : 'Continue to Checkout (TEST)'}
                  </button>
                ) : (
                  <button
                    onClick={handleTestAddSeatPack}
                    disabled={processing === 'test-add-seatpack' || !testSelectedDisciplemakerPack || testSelectedDisciplemakerPack === 'first-five'}
                    className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {processing === 'test-add-seatpack' ? 'Processing...' : 'Confirm — Use Card on File (TEST)'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OrganizationSubscription;
